import type { ClimateState, GameEvent, Sanctuary, World } from '../sim/types';
import { resultHtml } from './results';
import { isSurveyHome } from '../sim/operations';
import { plainTitle } from './surveyNames';
import type { Command, CommandResult } from '../sim/commands';
import { dateLabel, epochDayOf, seasonOf } from '../sim/calendar';
import { REGIONS, basin, region } from '../sim/data/oceans';
import { lightOf, reefOf, seafloorOf, waterOf } from '../sim/data/sites';
import { REEF_ISLANDS, binHeatFallC, binSpan } from '../sim/reef';
import { depthOfShare } from '../sim/light';
import { DEPTH_ZONES, summarize, type Seafloor, type SeafloorSummary } from '../sim/seafloor';
import { PAUSED, SPEEDS, speedLabel, type SpeedSetting } from './speed';
import { coordinates, escapeHtml, fixed, hoursMinutes, signed, setMarkup } from './format';
import type { WildlifeCard } from './wildlife';
import { drawWorldMap, siteAt, siteCard, siteNow, taggedSites } from './worldMap';
import { taggedHtml } from './tagged';
import { tagRecords } from '../sim/tags';
import { MAX_NAME_LENGTH } from '../sim/commands';
import { humpbackCard, humpbackWays } from './migrants';
import type { SiteId } from '../sim/data/sites';
import { BULLETHEAD, fishCounts } from '../sim/fish';
import { heatwaveToday, phaseName, regionsObserved, sanctuaryRecord } from '../sim/systems/climate';
import { heatStressToday } from '../sim/systems/heatStress';
import { ALERT_MEANINGS, degreeHeatingWeeks } from '../sim/heatStress';
import { categoryOf } from '../sim/heatwave';
import { ensoYearLabel as years } from '../sim/enso';
import { GameMenu } from './menu';
import { fishFigures, paintSprites } from './sprites';
import { communitiesOf, fieldGuide, managementOf, overviewOf, priorities, speciesRecord, type Indicator } from './overview';
import { backHtml, communityHtml, guideHtml, guideSearchHtml, indicatorHtml, managementHtml, overviewHtml, speciesHtml, viewName, type CardView } from './overviewPanels';
import { COMMUNITIES, SPECIES, displayName, speciesOf } from '../sim/species';
import { PLAYBACK_LENGTHS_S, PLAYBACK_S, PRESETS, PRESET_NAMES, type Preset } from './observation';
import { PANES, PANE_NAMES, dragEnd, dragHeight, handlePress, layoutFor, navPress, type Layout, type Pane, type SheetState } from './sheet';
import { NAV_ICONS, exploreHtml, objectiveHtml, objectiveMet, objectiveOf, managementYear } from './shell';

/** What Watch One Day's controls ask (M96; the camera's lock, M104). */
export type WatchAction = 'start' | 'pause' | 'resume' | 'replay' | 'exit' | 'lock' | 'unlock';

/** A watched day's length as its button names it. */
const lengthLabel = (s: number): string => (s < 120 ? `${s} s` : `${s / 60} min`);
import { HUMPBACK, whaleCounts, type WhaleCounts } from '../sim/whales';
import { atSeaText, defaultCrew, operationsHtml, planFor, operationsSummaryHtml, pendingRules, surveyHtml, type SurveyPlan } from './operationsPanel';
import { reefHealthHtml } from './reefHealth';
import { coralReportHtml, findingsHtml, homeHtml, lastSurvey } from './reports';
import { SHORE_DAYS, SURVEY_DAYS } from './goals';
import { netsShore, nextStretch, tripRefusal, tripsOf } from './reefCard';
import { statusStripHtml } from './statusStrip';
import { DEFAULT_AREA_PLAN, buildHtml, monthCloseHtml, monthReportHtml, operationsQueueHtml, type AreaPlan } from './areasPanel';
import { ECONOMY, type Role, type SurveyKind } from '../sim/economy';
import type { LimitGroup } from '../sim/management';
import type { ReefIsland } from '../sim/reef';
import { campaignHtml, campaignSummaryHtml, markOf, reviewHtml } from './campaignPanel';

/**
 * The HUD: DOM panels over the canvas. Replaceable.
 *
 * It reads the world and sends commands; it never writes to the world. Panels
 * redraw when the day changes or when something the player did changed them,
 * not every frame.
 *
 * **Buttons are built once and never redrawn** (M9). The first version rebuilt
 * the top bar's markup every simulated day, so a button pressed as a day turned
 * was gone by the time it was released, and the browser dropped the click:
 * MEASURED, 5 presses of 40 taken on a desktop with a 120 ms press. Now a day
 * changes only text and the highlight, and the elements under the pointer stay
 * the same elements.
 */

export interface HudHandlers {
  setSpeed(speed: SpeedSetting): void;
  command(command: Command): CommandResult;
  save(): string;
  load(): string;
  /** End the game in progress and begin a new one (the menu's New game, M63); what to tell the player. */
  newGame(): string;
  /** The aquarium drawn the other way, the real map or the exhibit (M86, M94); the way it is now drawn. */
  switchView(): AquariumStyle;
  /** The exhibit's camera taken to a habitat, or the whole tank (M94); the one it is on. */
  chooseHabitat(id: string): string;
  /** The Reef as it was on the player's first day, or today's again (M287), an island's or the whole. */
  chooseReefThen?(on: boolean, island: ReefIsland | null): string;
  /** The whole tank in view at once (M105, Fit All); the habitat it is on, the whole tank's. */
  fitAll(): string;
  /** A habitat scene's camera back at its own home view (M110, Reset View). */
  resetView(): void;
  /** The aquarium's hour set to a preset (M95); the preset chosen. */
  observe(preset: Preset): Preset;
  /** Watch One Day started, paused, resumed, replayed or ended (M96). */
  watch(action: WatchAction): void;
  /** A watched day's length chosen, real seconds (M104); the one in force. */
  watchLength(seconds: number): number;
  /** The school followed let go (M97, Follow Wildlife). */
  unfollow(): void;
  /** The animal followed followed on into the habitat it is crossing to (M147, the card's way on). */
  followOnward(): void;
  /** A tagged animal gone to in the habitat its tag tells it is in today (M203, the Tagged list's way); why not, where it cannot be. */
  goToTag(id: number): string | null;
  /** The aquarium of a site of the seven visited, or the player's own again with null (M98, the world map). */
  visit(siteId: SiteId | null): void;
  /** Whether the frame rate meter is shown (M166). */
  frameMeterOn(): boolean;
  /** Run the days on to the next event, then pause (M240); none until the game has events to run to. */
  nextEvent?(): void;
  /** The game saved in the browser now (M237's menu); what to tell the player. */
  saveNow?(): string;
  /** Whether the developer's tools are shown (M237: the frame rate meter among them). */
  devMode?(): boolean;
  /** An objective's own action (M239's campaign), where the HUD does not handle it. */
  objective?(act: string): void;
  /** Run the days on to an epoch day, then pause (M262: a survey's return, the objective's "Advance to its return"); stopped early on a warning, as the next event's. */
  advanceTo?(day: number, what: string): void;
  /** The frame rate meter turned on or off from the menu (M166); whether it is now on. */
  toggleFrameMeter(): boolean;
}

/**
 * How the aquarium is drawn: its real seafloor, or the exhibit (M94, the user's brief; in place of M86's
 * diorama, the user's word then: "keep both aquarium views that i can toggle between").
 */
export type AquariumStyle = 'map' | 'exhibit';

/** A habitat the exhibit's camera can go to: its id and its name. */
export interface HabitatChoice {
  id: string;
  name: string;
}

export class Hud {
  private readonly top: HTMLElement;
  private readonly card: HTMLElement;
  private readonly regionsCard: HTMLElement;
  /** The regions card's day-to-day parts: its note and its table's rows. Its World map button is built once (review M116: rebuilt each day, a press across a day's turn was lost, M9's mechanism). */
  private readonly regionsNote: HTMLElement;
  private readonly regionsRows: HTMLElement;
  private readonly menu: GameMenu;
  /** The speed the clock ran at when the menu opened, given back when it closes. */
  private speedBeforeMenu: SpeedSetting = 1;
  private readonly toast: HTMLElement;
  private readonly root: HTMLElement;
  private readonly nameEl: HTMLElement;
  private readonly dateEl: HTMLElement;
  private readonly speedButtons: HTMLButtonElement[];
  private readonly playButton: HTMLButtonElement;
  private readonly yearEl: HTMLElement;
  private readonly speedPick: HTMLButtonElement;
  /** The speed the play button gives back (the last one not paused). */
  private lastRunning: SpeedSetting = 1;
  /** Explore's pane, drawn each day (M237). */
  private readonly explore: HTMLElement;
  private exploreHtml = '';
  /** The destination shown and the sheet's state (M237, `sheet.ts`). */
  private pane: Pane = 'explore';
  private sheetState: SheetState = 'collapsed';
  /** The habitats of the exhibit, for Explore's cards, and the one shown. */
  private habitatChoices: readonly HabitatChoice[] = [];
  private habitatNow = '';
  /** The objective card's markup as last set. */
  private objectiveHtml = '';
  /** The objective last drawn, to tell when one is met (M262). */
  private objectiveTitle: string | null = null;
  private objectiveId: string | undefined;
  private objectiveWorld: World | null = null;
  private readonly cardTitle: HTMLElement;
  private readonly cardRename: HTMLButtonElement;
  private readonly cardBody: HTMLElement;
  private toastTimer: number | undefined;
  /** The card's pages, the overview first and the one shown last (M160); the page last drawn; the field guide's search. */
  private cardViews: CardView[] = [{ kind: 'overview' }];
  private cardDrawn = '';
  private guideSearch = '';
  /** The species of the animal followed, for its field guide page (M160), or none. */
  private wildlifeSpecies: string | null = null;
  /** The card's markup as last set, so a day that changes none of it leaves its buttons be. */
  private cardHtml = '';
  /** A press on the card under way, its redraw held till it ends (M9's rule for buttons redrawn: a press across a day's turn was lost); one waiting. */
  private cardPressed = false;
  private cardWaiting = false;
  private cardRelease: number | undefined;
  /** Each page's scroll as the player left it for the next, given back on the way back. */
  private cardScrolls: number[] = [];
  /**
   * The box the card scrolls in: the panel's `.sheet-body` on a phone and a computer alike (M291, the roadmap's R1: the
   * scroll had been set and kept on the card, which never scrolls, so each page opened where the panel was last left).
   */
  private get scroller(): { scrollTop: number } {
    return (typeof this.card.closest === 'function' ? this.card.closest<HTMLElement>('.sheet-body') : null) ?? this.card;
  }
  /** The field guide's list as last drawn, so a day that changes none of it leaves it be. */
  private guideHtml = '';
  /** The last `insets()` measurement, until something that moves it happens. */
  private measured: { top: number; right: number; bottom: number; left: number } | null = null;
  private lastDay = -1;
  private speed: SpeedSetting = 1;
  /** How the aquarium is drawn, from the view (`AquariumView.scaleNote`), or null. */
  private drawingNote: string | null = null;
  /** The aquarium's rendering choices, the exhibit's (M104), said after the note. */
  private drawingChoices = '';
  /** Whether the Drawn row reads against the depth ruler: the whole tank's, not a habitat's own scene (M110). */
  private drawingRuler = true;
  /** The player's own sanctuary's site, and the site picked on the world map (M98). */
  private ownSiteId: SiteId | undefined;
  private pickedSite: SiteId | null = null;
  /** What the card beside the map shows: a site's, the humpbacks' tracking, or nothing yet; filled again as the map opens and each day it is open (review M116: it kept the day it was picked, the old world's after a load). */
  private mapCard: 'site' | 'humpbacks' | null = null;
  /** The humpbacks' counts on the day (M101), for the world map's tracking card (M99); null where none are simulated. */
  private whaleNow: WhaleCounts | null = null;
  /** The world as last updated, for a site's rows today on the world map (M104). */
  private worldNow: World | null = null;
  /** The day's indicators (`indicatorsOf`), or none until drawn. */
  private indicators: { day: number; list: Indicator[] } | null = null;
  /** The survey being planned on the operations page (M238), or none until it is opened. */
  private plan: SurveyPlan | null = null;
  /** The day each island's patrol began in this world (M295), for its "Done" (the world keeps no patrol's first day). */
  private readonly patrolStarts = new WeakMap<World, Partial<Record<ReefIsland, number>>>();
  /** The top bar's status strip (M294) and its markup as last drawn. */
  private statusEl!: HTMLElement;
  /** A press under way on the objective card or the status strip, their redraw held till it is through (review 33). */
  private barPressed = false;
  private barWaiting = false;
  private barRelease = 0;
  private statusHtml = '';

  /**
   * An island chosen in the view (M292): Manage's card follows, its page the island's or the home's, without opening the
   * panel over the view.
   */
  chooseIsland(island: ReefIsland | null): void {
    // The reef's own card whatever island the view was pressed on (M327, one reef).
    void island;
    this.cardViews = [{ kind: 'overview' }];
    this.cardScrolls = [];
    if (this.worldNow) this.drawCard(this.worldNow);
  }
  /** The survey an island's card sends (M274): its days and divers. */
  private areaPlan: AreaPlan = DEFAULT_AREA_PLAN;
  /** What a tag of the animal followed would take (M148), or none. */
  private wildlifeTag: WildlifeCard['tag'] | null = null;
  private aquariumStyle: AquariumStyle = 'map';
  /** Whether a habitat's own scene is shown (M110, `setScene`). */
  private inScene = false;
  /** Whether the 3D view is drawn (M288, `setGraphics`). */
  private graphics = true;

  constructor(root: HTMLElement, private readonly handlers: HudHandlers) {
    this.root = root;
    // The overhaul's shell (M237): a slim top bar, the ocean, and the panel for Explore, Manage and World, a bottom sheet
    // on a phone held upright and a side panel on a wider screen (`sheet.ts`'s rules); the objective card at its head.
    root.dataset.pane = 'explore';
    root.dataset.sheet = 'collapsed';
    root.dataset.layout = layoutFor(window.innerWidth, window.innerHeight);
    root.innerHTML = `
      <header class="topbar"></header>
      <div class="aquarium-bar">
        <span class="view-pick">
          <button class="habitat-toggle" data-act="views" aria-expanded="false" aria-haspopup="true"><span class="habitat-name">The aquarium</span><span class="chev" aria-hidden="true">▾</span></button>
          <span class="view-menu popover" hidden>
            <span class="habitats" hidden></span>
            <span class="view-tools">
              <button class="fit-all" data-act="fit" title="The whole tank in view at once" hidden>Fit all</button>
              <button class="reset-view" data-act="reset" title="The habitat's own view again" hidden>Reset view</button>
              <button class="view-toggle" data-act="view" title="Draw the aquarium the other way"></button>
            </span>
          </span>
        </span>
        <span class="observe">
          <button class="observe-toggle" data-act="observe" title="The hour the aquarium is drawn at, apart from the date" aria-expanded="false"><span class="long"></span><span class="clock"></span></button>
          <span class="observe-menu popover" hidden>
            <span class="presets">${PRESETS.map((p) => `<button data-preset="${p}">${PRESET_NAMES[p]}</button>`).join('')}</span>
            <button class="watch-start" data-watch="start">Watch one day</button>
            <span class="lengths" aria-label="How long a watched day takes">${PLAYBACK_LENGTHS_S.map((s) => `<button data-length="${s}"${s === PLAYBACK_S ? ' class="on"' : ''}>${lengthLabel(s)}</button>`).join('')}</span>
            <span class="observe-where"></span>
            <span class="observe-note">The aquarium's hour, apart from the game's date: the days run on while it stays. Watch one day plays a whole day in <span class="watch-length">90 seconds</span>, the game held. Night drawn brighter than it is, so it can be seen, the moon's light by its phase and its height on the ecliptic (its 5° tilt aside). <span class="moon"></span></span>
          </span>
        </span>
        <span class="watch" hidden>
          <progress class="watch-progress" max="1" value="0"></progress>
          <button data-watch="pause">Pause</button>
          <button data-watch="replay">Replay</button>
          <button data-watch="lock" title="Hold the camera on what it frames for the rest of the day">Lock camera</button>
          <button data-watch="exit">Exit</button>
        </span>
        <span class="visit-note" hidden><span class="visit-text"></span> <button data-act="home"></button></span>
        <button class="at-sea chip" data-act="at-sea" hidden><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 15h18l-3 4H6z"/><path d="M8 15V8l6 3-6 0"/><path d="M2 21c2 0 2-1 4-1s2 1 4 1 2-1 4-1 2 1 4 1 2-1 4-1"/></svg><span class="at-sea-text"></span></button>
      </div>
      <div class="world-map" hidden role="dialog" aria-label="The world map">
        <div class="world-head"><strong>The world ocean</strong><span><button data-act="track">Humpback whales</button> <button data-act="world-close">Close</button></span></div>
        <canvas class="world-canvas"></canvas>
        <aside class="world-site" hidden><strong class="world-site-title"></strong><dl></dl><button data-act="go"></button></aside>
        <p class="world-note">The twelve regions the simulation steps (the World Ocean Atlas 2023's basins), the lines the surface water's exchange between them (NOAA's drifters, 1979 to 2025, wider for more), the land Natural Earth's. Gold: your sanctuary; blue: an aquarium to visit; gray: not built yet; orange: the Hawaiʻi humpbacks' ways to their feeding grounds. Pick a site.</p>
      </div>
      <aside class="wildlife" hidden aria-live="polite">
        <header><strong class="wildlife-title"></strong><button data-act="unfollow" aria-label="Stop following">Stop following</button></header>
        <div class="follow-clock" role="group" aria-label="The clock while following"><button data-follow-speed="1" title="Days at 1×, five seconds each">Normal</button><button data-follow-speed="0.25" title="Days at ¼×, twenty seconds each">Slow</button><button data-follow-speed="0" title="The days stopped, the animals swimming on">Observe</button></div>
        <dl class="wildlife-rows"></dl>
        <button data-act="route" hidden>Its ways on the world map</button>
        <button data-act="onward" hidden></button>
        <button data-act="guide" hidden>In the field guide</button>
        <form class="tag-form" hidden><input class="tag-name" maxlength="${MAX_NAME_LENGTH}" placeholder="A name" aria-label="A name for it"><button data-act="tag">Tag one</button><button type="button" data-act="name-school" hidden>Name the school</button></form>
      </aside>
      <section class="sheet" aria-label="Sanctuary panel">
        <div class="sheet-head"><button class="sheet-handle" data-act="handle" aria-label="Resize the panel"><span aria-hidden="true"></span></button><button class="sheet-close" data-act="sheet-close">Close</button></div>
        <div class="objective"></div>
        <div class="sheet-body">
          <div class="pane" data-pane="explore">
            <div class="explore"></div>
          </div>
          <div class="pane" data-pane="manage" hidden>
            <aside class="card sanctuary"></aside>
          </div>
          <div class="pane" data-pane="world" hidden>
            <aside class="card regions"></aside>
          </div>
        </div>
        <nav class="tabs" aria-label="Where to">
          ${PANES.map((p) => `<button data-pane="${p}"${p === 'explore' ? ' class="on"' : ''}>${NAV_ICONS[p]}<span>${PANE_NAMES[p]}</span></button>`).join('')}
        </nav>
      </section>
      <div class="toast" hidden></div>`;
    // The three destinations (M237): another opens the panel on it, the one shown folds the panel away or opens it.
    root.querySelector('.tabs')?.addEventListener('click', (event) => {
      const pane = (event.target as HTMLElement).closest<HTMLElement>('[data-pane]')?.dataset.pane as Pane | undefined;
      if (pane === undefined) return;
      const step = navPress(this.pane, this.sheetState, pane, this.layout);
      this.showPane(step.pane, step.state);
    });
    // The panel's own Close (M304, F3: "a large close/collapse button"), the reef whole again.
    root.querySelector('.sheet-close')?.addEventListener('click', () => this.showPane(this.pane, 'collapsed'));
    window.addEventListener('resize', () => {
      this.measured = null;
      const layout = layoutFor(window.innerWidth, window.innerHeight);
      if (root.dataset.layout !== layout) root.dataset.layout = layout;
    });
    this.bindSheet(root);
    this.top = root.querySelector('.topbar') as HTMLElement;
    this.card = root.querySelector('.sanctuary') as HTMLElement;
    this.regionsCard = root.querySelector('.regions') as HTMLElement;
    this.regionsCard.innerHTML = `
      <h2>The world ocean</h2>
      <button class="world-open primary" data-act="world" title="The seven seas' sanctuaries, the regions and their currents">Open the world map</button>
      <p class="note"></p>
      <table><thead><tr><th>Region</th><th>Season</th><th class="num">Day length</th><th class="num">Sea</th></tr></thead><tbody></tbody></table>
      <section class="tagged-list"></section>`;
    this.regionsNote = this.regionsCard.querySelector('.note') as HTMLElement;
    this.regionsRows = this.regionsCard.querySelector('tbody') as HTMLElement;
    this.toast = root.querySelector('.toast') as HTMLElement;
    // A tip's Hide tips (M262: "Allow tutorials to be dismissed or skipped"): no more tips in this browser.
    this.toast.addEventListener('click', (event) => {
      if ((event.target as HTMLElement).closest('[data-act="tips-off"]') === null) return;
      try {
        const seen = JSON.parse(localStorage.getItem(TIPS_KEY) ?? '{}') as Record<string, boolean>;
        localStorage.setItem(TIPS_KEY, JSON.stringify({ ...seen, off: true }));
      } catch {
        // No storage: the tips shown once a session anyway.
      }
      this.toast.hidden = true;
    });
    root.querySelector('.view-toggle')?.addEventListener('click', () => this.setView(this.handlers.switchView()));
    // The view's menu (M237): the habitats, Fit all, Reset view and the other way of drawing, behind the habitat's name.
    const views = root.querySelector<HTMLElement>('.habitat-toggle');
    const viewMenu = root.querySelector<HTMLElement>('.view-menu');
    views?.addEventListener('click', () => this.togglePopover(viewMenu, views));
    // The observation clock's presets (M95), in a small panel opened from the hour's button.
    const observe = root.querySelector<HTMLElement>('.observe-toggle');
    const observeMenu = root.querySelector<HTMLElement>('.observe-menu');
    observe?.addEventListener('click', () => this.togglePopover(observeMenu, observe));
    // A popover shut by a press anywhere else, or by Escape (`escape`).
    document.addEventListener('pointerdown', (event) => {
      const target = event.target as Node;
      for (const [menu, button] of [[viewMenu, views], [observeMenu, observe], [this.root.querySelector<HTMLElement>('.speed-menu'), this.root.querySelector<HTMLElement>('.speed-pick')]] as const) {
        if (menu && !menu.hidden && !menu.contains(target) && !button?.contains(target)) this.togglePopover(menu, button, false);
      }
    });
    observeMenu?.addEventListener('click', (event) => {
      const length = (event.target as HTMLElement).dataset.length;
      if (length !== undefined) {
        const chosen = this.handlers.watchLength(Number(length));
        for (const b of observeMenu.querySelectorAll<HTMLElement>('.lengths button')) b.classList.toggle('on', Number(b.dataset.length) === chosen);
        setText(observeMenu.querySelector<HTMLElement>('.watch-length') as HTMLElement, chosen < 120 ? `${chosen} seconds` : `${chosen / 60} minutes`);
        return;
      }
      const preset = (event.target as HTMLElement).dataset.preset as Preset | undefined;
      if (preset === undefined) return;
      const chosen = this.handlers.observe(preset);
      for (const b of observeMenu.querySelectorAll<HTMLElement>('.presets button')) b.classList.toggle('on', b.dataset.preset === chosen);
      // Its label's new length may wrap the bar (M166's review: the frame rate meter left under a new row).
      this.measured = null;
    });
    // Watch One Day's controls (M96): started from the hour's panel, then played, paused, replayed or left.
    for (const el of root.querySelectorAll<HTMLElement>('.watch, .watch-start')) {
      el.addEventListener('click', (event) => {
        const action = (event.target as HTMLElement).dataset.watch;
        if (action === undefined) return;
        if (action === 'start' && observeMenu) {
          observeMenu.hidden = true;
          observe?.setAttribute('aria-expanded', 'false');
        }
        // Its Pause button turns to Resume and back, and its Lock to Unlock, with the next frame's `setWatching`.
        this.handlers.watch(action as WatchAction);
        this.measured = null;
      });
    }
    // The world map (M98): opened and closed, a site picked on it, its aquarium gone to.
    const map = root.querySelector<HTMLElement>('.world-map');
    this.regionsCard.addEventListener('click', (event) => {
      if ((event.target as HTMLElement).closest('[data-act="world"]')) this.showWorldMap(true);
      // The Tagged list's way to a tagged animal (M203).
      this.goToTagged(event.target as HTMLElement);
    });
    root.querySelector('.world-map button[data-act="world-close"]')?.addEventListener('click', () => this.showWorldMap(false));
    // The first migrant tracked (M99): its card in the site's place, its ways drawn on the map.
    root.querySelector('.world-map button[data-act="track"]')?.addEventListener('click', () => this.trackHumpbacks());
    root.querySelector('.wildlife button[data-act="onward"]')?.addEventListener('click', () => this.handlers.followOnward());
    // The animal's page in the field guide (M160), on the sanctuary's card.
    root.querySelector('.wildlife button[data-act="guide"]')?.addEventListener('click', () => {
      if (this.wildlifeSpecies) this.openPage({ kind: 'species', id: this.wildlifeSpecies });
    });
    root.querySelector('.wildlife button[data-act="route"]')?.addEventListener('click', () => {
      this.showWorldMap(true);
      this.trackHumpbacks();
    });
    map?.querySelector('canvas')?.addEventListener('click', (event) => {
      const canvas = event.target as HTMLCanvasElement;
      const r = canvas.getBoundingClientRect();
      const picked = siteAt(event.clientX - r.left, event.clientY - r.top, r.width, r.height, 18);
      if (picked) this.pickSite(picked);
    });
    map?.querySelector('button[data-act="go"]')?.addEventListener('click', () => {
      if (!this.pickedSite) return;
      this.handlers.visit(this.pickedSite === this.ownSiteId ? null : this.pickedSite);
      this.showWorldMap(false);
    });
    root.querySelector('.visit-note button[data-act="home"]')?.addEventListener('click', () => this.handlers.visit(null));
    root.querySelector('.at-sea')?.addEventListener('click', () => this.objectiveAction('operations'));
    // Follow Wildlife's card (M97): its Stop following lets the school go.
    root.querySelector('.wildlife button[data-act="unfollow"]')?.addEventListener('click', () => this.handlers.unfollow());
    // Its clock (M266, Follow Animal): the days at their own pace, slowed, or stopped while the animals swim on.
    for (const b of root.querySelectorAll<HTMLButtonElement>('.wildlife [data-follow-speed]')) b.addEventListener('click', () => this.handlers.setSpeed(Number(b.dataset.followSpeed) as SpeedSetting));
    // Its tag (M148): one of the followed kind tagged and named, or its school named, through the command boundary.
    const tagForm = root.querySelector<HTMLFormElement>('.wildlife .tag-form');
    const tagIt = (school: boolean): void => {
      const tag = this.wildlifeTag;
      const input = tagForm?.querySelector<HTMLInputElement>('.tag-name');
      if (!tag || !input || !this.ownSiteId) return;
      const result = this.handlers.command(school
        ? { type: 'tag_group', siteId: this.ownSiteId, kind: 'school', species: tag.species, island: tag.island, name: input.value }
        : { type: 'tag_animal', siteId: this.ownSiteId, ...tag, name: input.value });
      this.say(result.message);
      if (result.ok) input.value = '';
      this.invalidate();
    };
    tagForm?.addEventListener('submit', (event) => { event.preventDefault(); tagIt(false); });
    tagForm?.querySelector('button[data-act="name-school"]')?.addEventListener('click', () => tagIt(true));
    // A habitat chosen from the view's menu or from Explore's cards (M237), the menu shut behind it.
    const pickHabitat = (event: Event): void => {
      const id = (event.target as HTMLElement).closest<HTMLElement>('[data-habitat]')?.dataset.habitat;
      if (id === undefined) return;
      this.togglePopover(viewMenu, views, false);
      this.pickHabitat(id);
    };
    root.querySelector('.habitats')?.addEventListener('click', pickHabitat);
    root.querySelector('.fit-all')?.addEventListener('click', () => {
      this.togglePopover(viewMenu, views, false);
      this.highlightHabitat(this.handlers.fitAll());
    });
    // A habitat scene's home view (M110).
    root.querySelector('.reset-view')?.addEventListener('click', () => {
      this.togglePopover(viewMenu, views, false);
      this.handlers.resetView();
    });
    // Explore's own controls (M237): its habitat cards, the field guide, the hour's presets and the world map.
    this.explore = root.querySelector('.explore') as HTMLElement;
    this.explore.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      if (target.closest('[data-habitat]')) {
        pickHabitat(event);
        if (this.layout === 'sheet') this.showPane(this.pane, 'collapsed');
        return;
      }
      const act = target.closest<HTMLElement>('[data-act]')?.dataset.act;
      if (act === 'guide') this.openPage({ kind: 'guide' });
      if (act === 'observe-open') this.togglePopover(observeMenu, observe, true);
      this.goToTagged(target);
    });
    // The objective card's action (M237).
    root.querySelector('.objective')?.addEventListener('click', (event) => {
      const act = (event.target as HTMLElement).closest<HTMLElement>('[data-objective]')?.dataset.objective;
      if (act) this.objectiveAction(act);
    });

    // The top bar and the card's heading, built once (see above).
    // The top bar (M237): where and when, the clock's compact controls, the menu. Save and Load in the menu now, the game
    // saved on its own (`main.ts`); the frame rate a developer's (M237's settings).
    this.top.innerHTML = `
      <span class="where"><span class="name"></span><span class="when"><span class="date"></span><span class="year"></span></span></span>
      <span class="clock-controls">
        <button class="play" data-act="play" aria-label="Pause"></button>
        <span class="speed-pick-wrap">
          <button class="speed-pick" data-act="speeds" aria-expanded="false" aria-haspopup="true" aria-label="Game speed"></button>
          <span class="speed-menu popover" hidden><span class="speeds">${SPEEDS.map((s) => `<button class="speed" data-speed="${s}">${speedLabel(s)}</button>`).join('')}</span></span>
        </span>
        <button class="next-event" data-act="next" hidden title="Run the days on to the next event, then pause">Next event</button>
      </span>
      <span class="files"><button class="what-next" data-act="what-next" title="What should I do next?" aria-label="What should I do next?">?</button><button class="menu-button" data-act="menu" aria-label="Menu"><span aria-hidden="true">☰</span></button></span>
      <span class="status-strip" aria-label="Funds, boats and work under way"></span>`;
    this.nameEl = this.top.querySelector('.name') as HTMLElement;
    this.dateEl = this.top.querySelector('.date') as HTMLElement;
    this.yearEl = this.top.querySelector('.year') as HTMLElement;
    this.speedButtons = [...this.top.querySelectorAll<HTMLButtonElement>('.speeds button')];
    this.playButton = this.top.querySelector('.play') as HTMLButtonElement;
    this.speedPick = this.top.querySelector('.speed-pick') as HTMLButtonElement;
    this.statusEl = this.top.querySelector('.status-strip') as HTMLElement;
    // A press on the objective card or the status strip holds their redraw till its click has gone through, as the card's
    // own (review 33: "While the boat is out"'s acts and the strip's buttons were drawn again each day the money or the
    // wait moved, a press at speed lost as M9's were).
    for (const box of [this.root.querySelector<HTMLElement>('.objective'), this.statusEl]) {
      box?.addEventListener('pointerdown', (event) => {
        if (event.button !== 0) return;
        this.barPressed = true;
        window.clearTimeout(this.barRelease);
      });
      box?.addEventListener('click', () => window.setTimeout(() => this.releaseBar(), 0), true);
    }
    for (const type of ['pointerup', 'pointercancel']) {
      window.addEventListener(type, () => {
        if (!this.barPressed) return;
        window.clearTimeout(this.barRelease);
        this.barRelease = window.setTimeout(() => this.releaseBar(), CARD_RELEASE_MS);
      });
    }
    this.card.innerHTML = `
      <h2><span class="title"></span> <button class="rename" data-act="rename">Rename</button></h2>
      <div class="body"></div>`;
    this.cardTitle = this.card.querySelector('.title') as HTMLElement;
    this.cardRename = this.card.querySelector('.rename') as HTMLButtonElement;
    this.cardBody = this.card.querySelector('.body') as HTMLElement;
    this.highlightSpeed();
    this.top.addEventListener('click', (event) => this.onTopClick(event));
    this.card.addEventListener('click', (event) => this.onCardClick(event));
    // A press on the card holds its redraw till its click has gone through (M160: its pages' buttons are drawn each day,
    // M9's lost press), or, where none follows (a drag off the card, its scroll bar, a right press), a moment after the
    // pointer is let go anywhere (its review: released on the card's own pointerup, a tap's later click could land on
    // the redrawn page, and a press on the scroll bar held the card's figures till the pointer left it).
    // The main button's press only (its second review: a right press's menu took its release, the card held till the
    // next press anywhere).
    this.card.addEventListener('pointerdown', (event) => {
      if (event.button !== 0) return;
      this.cardPressed = true;
      window.clearTimeout(this.cardRelease);
    });
    this.card.addEventListener('click', () => this.releaseCard(), true);
    for (const type of ['pointerup', 'pointercancel']) {
      window.addEventListener(type, () => {
        if (!this.cardPressed) return;
        window.clearTimeout(this.cardRelease);
        this.cardRelease = window.setTimeout(() => this.releaseCard(), CARD_RELEASE_MS);
      });
    }
    // The field guide's search (M160): its list drawn again as the player types, the box kept.
    this.card.addEventListener('input', (event) => {
      const input = event.target as HTMLInputElement;
      if (!input.classList.contains('guide-search') || !this.worldNow) return;
      this.guideSearch = input.value;
      const results = this.cardBody.querySelector<HTMLElement>('.guide-results');
      if (results) results.innerHTML = this.guideHtml = guideHtml(fieldGuide(this.worldNow, this.guideSearch));
    });
    // The game menu (M63), over everything; the clock held while it is open.
    this.menu = new GameMenu(root, {
      // Pressed only where the clock changes: a press of the speed it already runs at is the player's own in the
      // game (a Pause during a watched day is kept when it ends, M96's review), never the menu's.
      opened: () => {
        this.speedBeforeMenu = this.speed;
        if (this.speed !== PAUSED) handlers.setSpeed(PAUSED);
      },
      closed: () => {
        if (this.speedBeforeMenu !== this.speed) handlers.setSpeed(this.speedBeforeMenu);
      },
      newGame: () => this.say(handlers.newGame()),
      fieldGuide: () => this.openPage({ kind: 'guide' }),
      save: () => this.say(handlers.save()),
      load: () => this.say(handlers.load()),
      devMode: () => handlers.devMode?.() ?? false,
      frameMeterOn: () => handlers.frameMeterOn(),
      toggleFrameMeter: () => handlers.toggleFrameMeter(),
    });
  }

  /**
   * The speed the menu gives back when it closes, where the game set one while it was open (M96's review: a new game
   * started from the menu during a watch gave back the watch's pause, not the speed it held).
   */
  setSpeedAfterMenu(speed: SpeedSetting): void {
    this.speedBeforeMenu = speed;
  }

  /** Whether the game menu is open: the clock's keys wait while it is. */
  get menuOpen(): boolean {
    return this.menu.isOpen;
  }

  /** The Escape key: an open popover shut, else the menu steps back, or closes. */
  escape(): void {
    if (!this.menu.isOpen && this.shutPopovers()) return;
    this.menu.press('escape');
  }

  setSpeed(speed: SpeedSetting): void {
    this.speed = speed;
    this.highlightSpeed();
  }

  /** Lights the rung in force, at once rather than at the next day. */
  private highlightSpeed(): void {
    for (const b of this.speedButtons) b.classList.toggle('on', Number(b.dataset.speed) === this.speed);
    this.highlightFollowClock();
    if (this.speed !== PAUSED) this.lastRunning = this.speed;
    const paused = this.speed === PAUSED;
    setText(this.playButton, paused ? '▶' : '❚❚');
    this.playButton.setAttribute('aria-label', paused ? 'Play' : 'Pause');
    this.playButton.classList.toggle('paused', paused);
    setText(this.speedPick, speedLabel(this.lastRunning));
  }

  /** The followed animal's card's clock lit as the top bar's (M266): none while an advance runs the days, its pause the advance's. */
  private highlightFollowClock(): void {
    for (const b of this.root.querySelectorAll<HTMLButtonElement>('.wildlife [data-follow-speed]')) b.classList.toggle('on', !this.advancing && Number(b.dataset.followSpeed) === this.speed);
  }

  /** Called every frame; does work only when the day has moved or a redraw is due. */
  update(world: World): void {
    this.ownSiteId = world.sanctuaries[0]?.siteId;
    this.worldNow = world;
    if (world.day === this.lastDay) return;
    this.lastDay = world.day;
    const whales = world.sanctuaries[0]?.whales;
    this.whaleNow = whales ? whaleCounts(HUMPBACK, whales.humpback, epochDayOf(world)) : null;
    this.drawTop(world);
    this.drawObjective(world);
    this.drawExplore(world);
    this.drawAtSea(world);
    this.drawCard(world);
    // The regions only while World is open (M237): twelve rows a day for a pane not seen.
    if (this.pane === 'world' && this.sheetState !== 'collapsed') this.drawRegions(world);
    if (this.root.querySelector<HTMLElement>('.world-map')?.hidden === false) this.fillMapCard();
    this.measured = null;
  }

  invalidate(): void {
    this.lastDay = -1;
    this.indicators = null;
  }

  /**
   * The overview's six indicators, computed once a day and shared by the objective card and the Manage card (M237's
   * probes: computed for each, the HUD's daily redraw cost about as much as half a tick at the top speeds).
   */
  private indicatorsOf(world: World): Indicator[] {
    if (this.indicators?.day !== world.day) this.indicators = { day: world.day, list: overviewOf(world) };
    return this.indicators.list;
  }

  /** The renderer's word on how the aquarium is stretched, shown in the sanctuary card. */
  setDrawingNote(note: string | null, choices = '', ruler = true): void {
    // The same note again draws nothing again (review M122: a habitat scene's, set as each day turns, had redrawn the
    // card a second time a day).
    if (note === this.drawingNote && choices === this.drawingChoices && ruler === this.drawingRuler) return;
    this.drawingNote = note;
    this.drawingChoices = choices;
    this.drawingRuler = ruler;
    this.invalidate();
  }

  /**
   * What the HUD covers along the top, right and bottom edges, so the camera
   * can center the aquarium in the rest: the top bar always; on a phone held
   * upright, the open sheet and the tabs under it; on a phone held sideways,
   * the open side panel and the tabs. On a wider screen the panels float
   * beside the aquarium: the map is framed over the whole canvas under the
   * top bar, the exhibit between the panels and under its habitats' bar (M94).
   *
   * **Measured only when it can have changed** (review M14): a redraw, a tab,
   * a resize. It was measured every frame, which on a phone at 128× forced a
   * layout pass dozens of times a second for a figure that moves once a day.
   * The top bar's bottom edge is also handed to the stylesheet
   * (`--topbar-bottom`), so the panels sit below the bar wherever it ends.
   */
  insets(): { top: number; right: number; bottom: number; left: number } {
    if (this.measured) return this.measured;
    const barBottom = this.top.getBoundingClientRect().bottom;
    this.root.style.setProperty('--topbar-bottom', `${Math.round(barBottom)}px`);
    const sheet = this.root.querySelector<HTMLElement>('.sheet');
    let [bottom, left, right] = [0, 0, 0];
    if (sheet) {
      const r = sheet.getBoundingClientRect();
      // The panel's edge over the ocean (M237): a sheet's top on a phone held upright, a side panel's right edge else.
      if (this.layout === 'sheet') bottom = Math.max(0, window.innerHeight - r.top);
      else left = Math.max(0, r.right);
    }
    this.root.style.setProperty('--panel-right', `${Math.round(right)}px`);
    this.root.style.setProperty('--panel-left', `${Math.round(left)}px`);
    // The wildlife card above what covers the aquarium's foot (M97).
    this.root.style.setProperty('--aquarium-bottom', `${Math.round(bottom)}px`);
    // The view's bar under the top bar counts too, so a habitat is framed below it (M94).
    const strip = this.root.querySelector<HTMLElement>('.aquarium-bar');
    const stripShown = strip !== null && getComputedStyle(strip).display !== 'none';
    const stripBottom = stripShown ? Math.max(barBottom, (strip as HTMLElement).getBoundingClientRect().bottom) : barBottom;
    const top = this.aquariumStyle === 'exhibit' ? Math.max(barBottom, stripBottom + 4) : barBottom;
    // And handed to the stylesheet, so the frame rate meter hangs below it, covering none of its buttons (M166).
    this.root.style.setProperty('--aquarium-bar-bottom', `${Math.round(stripBottom)}px`);
    this.measured = { top, right, bottom, left };
    return this.measured;
  }

  /** How the HUD is laid out now: a bottom sheet or a side panel (`sheet.ts`, M237). */
  private get layout(): Layout {
    return (this.root.dataset.layout as Layout | undefined) ?? 'sheet';
  }

  /** A destination shown in a state of the panel (M237): its pane drawn, the others hidden, the tab lit. */
  showPane(pane: Pane, state: SheetState): void {
    this.pane = pane;
    this.sheetState = state;
    this.root.dataset.pane = pane;
    this.root.dataset.sheet = state;
    for (const el of this.root.querySelectorAll<HTMLElement>('.pane')) el.hidden = el.dataset.pane !== pane;
    // The objective's button not drawn where it would open the pane already open.
    for (const b of this.root.querySelectorAll<HTMLElement>('.objective [data-objective]')) {
      if (b.dataset.objective === 'toggle') continue;
      b.hidden = b.dataset.objective === pane && state !== 'collapsed';
    }
    for (const b of this.root.querySelectorAll<HTMLElement>('.tabs button')) {
      const on = b.dataset.pane === pane && state !== 'collapsed';
      b.classList.toggle('on', on);
      if (on) b.setAttribute('aria-current', 'page');
      else b.removeAttribute('aria-current');
    }
    const sheet = this.root.querySelector<HTMLElement>('.sheet');
    sheet?.style.removeProperty('--sheet-drag');
    this.measured = null;
    if (pane === 'world' && state !== 'collapsed' && this.worldNow) this.drawRegions(this.worldNow);
  }

  /**
   * The sheet's handle (M237, the brief: "a draggable or otherwise intuitive collapsible bottom sheet"): dragged, it
   * follows the finger and settles in the state nearest where it is let go, or one on where flicked (`dragEnd`); tapped
   * or pressed from the keyboard, it goes one state on (`handlePress`). On a side panel it folds the panel and opens it.
   */
  private bindSheet(root: HTMLElement): void {
    const sheet = root.querySelector<HTMLElement>('.sheet');
    const handle = root.querySelector<HTMLElement>('.sheet-handle');
    if (!sheet || !handle) return;
    const heights = (): [number, number, number] => {
      const h = window.innerHeight;
      const top = this.top.getBoundingClientRect().bottom;
      const collapsed = (root.querySelector<HTMLElement>('.tabs')?.offsetHeight ?? 64) + (root.querySelector<HTMLElement>('.objective')?.offsetHeight ?? 0) + 26;
      return [collapsed, Math.max(collapsed, Math.round(h * 0.52)), Math.max(collapsed, Math.round(h - top - 8))];
    };
    let drag: { y: number; t: number; moved: boolean; id: number } | null = null;
    handle.addEventListener('pointerdown', (event) => {
      if (this.layout !== 'sheet') return;
      drag = { y: event.clientY, t: performance.now(), moved: false, id: event.pointerId };
      handle.setPointerCapture(event.pointerId);
    });
    handle.addEventListener('pointermove', (event) => {
      if (!drag || event.pointerId !== drag.id) return;
      const dy = event.clientY - drag.y;
      if (Math.abs(dy) > 4) drag.moved = true;
      if (drag.moved) sheet.style.setProperty('--sheet-drag', `${dragHeight(this.sheetState, heights(), dy)}px`);
    });
    const end = (event: PointerEvent): void => {
      if (!drag || event.pointerId !== drag.id) return;
      const was = drag;
      drag = null;
      if (!was.moved) return;
      handle.dataset.dragged = 'yes';
      this.showPane(this.pane, dragEnd(this.sheetState, heights(), event.clientY - was.y, performance.now() - was.t));
    };
    handle.addEventListener('pointerup', end);
    handle.addEventListener('pointercancel', end);
    handle.addEventListener('click', () => {
      // A drag's own click is not a tap.
      if (handle.dataset.dragged === 'yes') {
        delete handle.dataset.dragged;
        return;
      }
      if (this.layout === 'side') this.showPane(this.pane, this.sheetState === 'collapsed' ? 'half' : 'collapsed');
      else this.showPane(this.pane, handlePress(this.sheetState));
    });
  }

  /** A popover opened or shut beside its button (`open` to force one way), the others shut. */
  private togglePopover(menu: HTMLElement | null, button: HTMLElement | null | undefined, open?: boolean): void {
    if (!menu) return;
    const show = open ?? menu.hidden;
    if (show) {
      for (const other of this.root.querySelectorAll<HTMLElement>('.popover')) {
        if (other !== menu && !other.hidden) {
          other.hidden = true;
          other.parentElement?.querySelector('[aria-expanded]')?.setAttribute('aria-expanded', 'false');
        }
      }
    }
    menu.hidden = !show;
    button?.setAttribute('aria-expanded', String(show));
    this.measured = null;
  }

  /** Whether a popover is open: Escape shuts it first. */
  private shutPopovers(): boolean {
    let shut = false;
    for (const menu of this.root.querySelectorAll<HTMLElement>('.popover')) {
      if (menu.hidden) continue;
      menu.hidden = true;
      menu.parentElement?.querySelector('[aria-expanded]')?.setAttribute('aria-expanded', 'false');
      shut = true;
    }
    return shut;
  }

  /** The objective card's action (M237): where it sends the player. */
  private objectiveAction(act: string): void {
    if (act === 'toggle') {
      this.root.dataset.objective = this.root.dataset.objective === 'open' ? 'shut' : 'open';
      this.measured = null;
      return;
    }
    // The guide put away (M303, F2): the sanctuary's goals from here.
    if (act === 'skip-guide') {
      this.manage({ type: 'skip_guide' });
      return;
    }
    // Manage on a phone's sheet at its full height, as its tab opens it (M304's review: the card's act had opened it at half).
    if (act === 'manage' || act === 'explore' || act === 'world') this.showPane(act, this.sheetState === 'full' || (act === 'manage' && this.layout === 'sheet') ? 'full' : 'half');
    // The Reef dived into at once (M262: the first objective's "Dive in"), the panel folded out of the way, its controls
    // told the first time.
    else if (act === 'explore-reef') {
      this.highlightHabitat(this.handlers.chooseHabitat('reef'));
      this.markSeen('exploredReef');
      this.showPane('explore', 'collapsed');
      // Without the 3D view, its own words (M289, the playtest's third usability point: drag and zoom told where neither works).
      if (this.graphics) this.tip('dive', DIVE_TIP);
      else this.tip('dive-panel', DIVE_TIP_PANEL);
    }
    // A coral survey planned (M262: the warm season's "Plan a coral survey"), its kind chosen for the player.
    // M291 (the roadmap's R1, one route to each action): a survey is planned on its island's card, the coral's lit. The old survey page stays for the whole sanctuary's census,
    // under an island card's Advanced options.
    else if (act === 'survey-coral' || act === 'survey') {
      // The reef's coral survey lit on its card, nothing sent: the objective names no cost (M319's review; M327, one
      // reef: the whole reef's).
      this.objectiveAction('reef:survey:reefBenthic');
    }
    // The days run on to what the objective waits for (M262): the survey's return, else the next event.
    else if (act === 'advance') {
      const target = this.worldNow ? objectiveOf(this.worldNow, this.indicatorsOf(this.worldNow)).progress : undefined;
      if (target && this.handlers.advanceTo) this.handlers.advanceTo(target.day, target.what);
      else this.handlers.nextEvent?.();
    }
    // A page of Manage's (M239; M262: the coral's report, the survey, its findings, the response, opened directly).
    else if (PLAIN_PAGES.includes(act)) {
      this.showPane('manage', this.layout === 'sheet' ? 'full' : 'half');
      this.openPage({ kind: act } as CardView);
      // The page's own button lit: the one that does what the objective asks, never a goal's way back to the same page
      // (review 34: the heat's choice had lit its own goal's button, neither of its two ways).
      this.pulse(this.cardBody.querySelector('.primary:not([disabled]):not([data-act="goal"])'));
    }
    // The reef's ways (M327, one reef: `reef:<what>[:<kind>]`, and the boat-out offer's): each lit on the reef's card for
    // the player to choose (M296: one route to the decision; M329, O4: a trip too, on the boat's card beside the trips it
    // competes with, where M319 had sent it at once), the boat-out offer's sent; a trip that cannot go now lit all the
    // same, beside the others, and why told (its review: a lit disabled button had said nothing).
    // A patrol ended early, the month's way when the money is short (M329): sent at once, the month's card the choice.
    else if (act.startsWith('end-patrol:')) {
      this.manage({ type: 'set_patrol', sanctuaryId: this.cardSanctuary(), island: act.slice('end-patrol:'.length), level: 0 });
      this.showPane('manage', this.layout === 'sheet' ? 'full' : 'half');
      this.openPage({ kind: 'overview' });
    }
    else if (/^(reef|while-volunteers):/.test(act)) {
      const [kind, second, third, fourth] = act.split(':') as [string, string | undefined, string | undefined, string | undefined];
      const what = kind === 'reef' ? second : 'volunteers';
      const survey = kind === 'reef' ? third : undefined;
      const sends = kind !== 'reef';
      const done = what && sends ? this.reefAct(what, survey) : false;
      if (!done) {
        this.showPane('manage', this.layout === 'sheet' ? 'full' : 'half');
        this.openPage({ kind: 'overview' });
        const lit = what === 'protect' ? '[data-act="reef"][data-what="protect"]:not([disabled]), [data-act="reef"][data-what="patrol"]:not([disabled])'
          : what ? `[data-act="reef"][data-what="${what}"]${survey ? `[data-kind="${survey}"]` : ''}${fourth ? `[data-island="${fourth}"]` : ''}` : '.primary:not([disabled])';
        const button = this.cardBody.querySelector<HTMLButtonElement>(lit);
        this.pulse(button ?? this.cardBody.querySelector('.primary:not([disabled])'));
        const trip = button?.disabled && this.worldNow ? tripsOf(this.worldNow).find((t) => t.what === what && (what === 'cleanup' || (t.kind === survey && (t.island ?? undefined) === fourth))) : undefined;
        const why = trip && this.worldNow ? tripRefusal(this.worldNow, trip) : null;
        if (why) this.say(`Not now: ${why}.`);
      }
    }
    else this.handlers.objective?.(act);
  }

  /**
   * The aquarium shown whole on a phone, its sheet folded (M117: the Sanctuary card had stayed open over half the screen,
   * the Reef seen in a strip 191 px tall; M237's sheet); on a wider screen, where the panel stands beside the ocean,
   * nothing.
   */
  showAquarium(): void {
    if (this.layout !== 'sheet' || this.sheetState === 'collapsed') return;
    this.showPane(this.pane, 'collapsed');
  }

  /**
   * A control explained the first time it is met (M262, the brief's section 5: brief, contextual, dismissible, never
   * repeated): said once, remembered in this browser; none once the player has pressed a tip's Hide tips.
   */
  tip(key: string, message: string): void {
    try {
      const seen = JSON.parse(localStorage.getItem(TIPS_KEY) ?? '{}') as Record<string, boolean>;
      if (seen.off || seen[key]) return;
      seen[key] = true;
      localStorage.setItem(TIPS_KEY, JSON.stringify(seen));
    } catch {
      // No storage (a private window): told each time instead of never.
    }
    this.say(message, true);
  }

  say(text: string, tip = false): void {
    // Every toast in a player's words (M312's review: the core's own messages name the programs, "a reef benthic survey").
    const message = plainTitle(text);
    // A message that comes while another is fresh waits its turn (M262: the first dive's tip had been overwritten at once
    // by the objective's "Done"), each read for at least `TOAST_READ_MS`.
    const wait = this.toast.hidden ? 0 : this.toastShownAt + TOAST_READ_MS - performance.now();
    if (wait > 0) {
      this.toastQueue.push({ message, tip });
      if (this.toastQueue.length === 1) window.setTimeout(() => this.sayQueued(), wait);
      return;
    }
    this.toastShownAt = performance.now();
    if (tip) this.toast.innerHTML = `${escapeHtml(message)} <button class="link" data-act="tips-off">Hide tips</button>`;
    else this.toast.textContent = message;
    this.toast.hidden = false;
    // A second message restarts the clock rather than being cut short by the first's.
    window.clearTimeout(this.toastTimer);
    this.toastTimer = window.setTimeout(() => { this.toast.hidden = true; }, tip ? 6000 : 3500);
  }

  /** The next message waiting, shown; the rest after it. */
  private sayQueued(): void {
    const next = this.toastQueue.shift();
    if (!next) return;
    this.toast.hidden = true;
    this.say(next.message, next.tip);
    if (this.toastQueue.length > 0) window.setTimeout(() => this.sayQueued(), TOAST_READ_MS);
  }
  private toastQueue: { message: string; tip: boolean }[] = [];
  private toastShownAt = 0;

  /** The objective card (M237): what to do now, and its way to do it; its markup set only when it changes. */
  private drawObjective(world: World): void {
    const o = objectiveOf(world, this.indicatorsOf(world));
    // Another world (a New game, a Load): its objective is no step from the last one's (review M262's: a later save loaded
    // over a new game said "Done: Explore Maui Nui Reef").
    if (world !== this.objectiveWorld) [this.objectiveTitle, this.objectiveId, this.objectiveWorld] = [null, undefined, world];
    // An objective met (M262: "Clear feedback when the action is completed"): said, the next named, the card lit a moment.
    // Only an objective the campaign records met: a New game or a Load changes the objective without meeting it.
    if (this.objectiveTitle !== null && objectiveMet(world, this.objectiveId, o.id)) {
      this.say(`Done: ${this.objectiveTitle}. Next: ${o.title}.`);
      const box = this.root.querySelector<HTMLElement>('.objective');
      box?.classList.remove('met');
      void box?.offsetWidth;
      box?.classList.add('met');
    }
    this.objectiveTitle = o.title;
    this.objectiveId = o.id;
    const html = objectiveHtml(o);
    if (html === this.objectiveHtml) return;
    if (this.barPressed) {
      this.barWaiting = true;
      return;
    }
    this.objectiveHtml = html;
    const box = this.root.querySelector<HTMLElement>('.objective');
    if (box) box.innerHTML = html;
    const action = box?.querySelector<HTMLElement>('[data-objective]:not([data-objective="toggle"])');
    if (action) action.hidden = action.dataset.objective === this.pane && this.sheetState !== 'collapsed';
    this.measured = null;
  }

  /** Explore's pane (M237): the habitats, the hour, the field guide, the tagged; drawn only when it changes. */
  private drawExplore(world: World): void {
    const html = exploreHtml(world, this.habitatChoices, this.habitatNow, this.graphics);
    if (html === this.exploreHtml) return;
    this.exploreHtml = html;
    setMarkup(this.explore, html);
  }

  /**
   * The boat at sea (M240, the brief's section 6: operations seen): a chip on the view's bar while a survey is out, its
   * kind, its divers and the days left, pressed for Operations. Text set in place, the chip never rebuilt (M9).
   */
  private drawAtSea(world: World): void {
    const chip = this.root.querySelector<HTMLButtonElement>('.at-sea');
    if (!chip) return;
    const text = atSeaText(world);
    chip.hidden = text === null;
    if (text !== null) setText(chip.querySelector('.at-sea-text') as HTMLElement, text);
  }

  /** Where and when (M237): the sanctuary shown, the date and the year of its management (its first year the first). */
  private drawTop(world: World): void {
    const s = world.sanctuaries[0];
    setText(this.nameEl, s ? s.name.replace(/ Sanctuary$/, '') : world.conservancyName);
    this.nameEl.title = world.conservancyName;
    setText(this.dateEl, dateLabel(epochDayOf(world)));
    const year = s ? managementYear(world.startEpochDay + (s.establishedDay ?? 0), epochDayOf(world)) : 1;
    setText(this.yearEl, ` · Year ${year}`);
    // The status strip (M294, the one-island roadmap's R4): drawn where it changed, its height measured again if it did.
    const status = s ? statusStripHtml(world) : '';
    if (status !== this.statusHtml && this.barPressed) this.barWaiting = true;
    else if (status !== this.statusHtml) {
      this.statusHtml = status;
      this.statusEl.innerHTML = status;
      this.measured = null;
    }
  }

  private drawCard(world: World): void {
    const sanctuary = world.sanctuaries[0];
    if (!sanctuary) {
      setText(this.cardTitle, 'No sanctuary');
      this.cardRename.hidden = true;
      this.cardBody.innerHTML = '';
      this.cardHtml = '';
      return;
    }
    setText(this.cardTitle, sanctuary.name);
    // Renaming on the sanctuary's details alone (M262: a secondary page's).
    this.cardRename.hidden = this.cardViews.at(-1)?.kind !== 'details';
    this.cardRename.dataset.id = String(sanctuary.id);
    if (this.cardPressed) {
      this.cardWaiting = true;
      return;
    }
    this.cardWaiting = false;
    const view = this.cardViews[this.cardViews.length - 1] ?? { kind: 'overview' };
    const key = JSON.stringify(view);
    // The field guide's box kept as the days turn, only its list drawn again, so it keeps its focus and its words.
    const results = this.cardBody.querySelector<HTMLElement>('.guide-results');
    if (view.kind === 'guide' && key === this.cardDrawn && results) {
      const list = guideHtml(fieldGuide(world, this.guideSearch));
      if (list === this.guideHtml) return;
      const focused = this.focusedKey();
      results.innerHTML = this.guideHtml = list;
      this.refocus(focused);
      return;
    }
    const r = region(sanctuary.regionId);
    const latest = world.log[world.log.length - 1];
    const back = this.cardViews.length > 1 ? backHtml(viewName(this.cardViews[this.cardViews.length - 2] as CardView, this.pageNames(world))) : '';
    let page: string;
    switch (view.kind) {
      case 'overview':
        // Manage's home (M262): the sanctuary's status, its actions, the rest a press away.
        page = homeHtml(world, this.indicatorsOf(world), this.areaPlan, this.patrolStarts.get(world));
        break;
      case 'details':
        page = `<p class="place">${escapeHtml(sanctuary.site.placeName)}, ${coordinates(sanctuary.site.latDeg, sanctuary.site.lonDeg)}</p>
          ${operationsSummaryHtml(world)}
          ${campaignSummaryHtml(world)}
          ${overviewHtml(this.indicatorsOf(world), priorities(world), communitiesOf(world), managementOf(world) !== null)}`;
        break;
      case 'indicator': {
        const indicator = this.indicatorsOf(world).find((i) => i.id === view.id);
        page = indicator ? indicatorHtml(indicator, communitiesOf(world)) : '';
        break;
      }
      case 'community': {
        const c = communitiesOf(world).find((x) => x.id === view.id);
        page = c ? communityHtml(c) : '<p class="note">None of this community at the sanctuary now.</p>';
        break;
      }
      case 'species':
        // The bullethead's its sprites from the user's pictures, under what is known (M163).
        page = speciesHtml(speciesRecord(world, view.id), view.id === 'bullethead' ? bulletheadPicture(sanctuary, epochDayOf(world)) : '');
        break;
      case 'guide':
        page = guideSearchHtml(this.guideSearch);
        break;
      case 'management': {
        const m = managementOf(world);
        page = m ? managementHtml(m, pendingRules(world)) : '<p class="note">Nothing to manage at this sanctuary yet.</p>';
        break;
      }
      case 'campaign':
        page = campaignHtml(world);
        break;
      case 'review':
        page = reviewHtml(world);
        break;
      case 'operations':
        page = operationsHtml(world);
        break;
      case 'survey':
        // The survey being planned kept by the HUD (M238), only its crew still on the staff; a new one where it has none
        // left and the boat is home (review M238's: made while the crew were at sea, it came home empty).
        this.plan = planFor(world, this.plan);
        page = surveyHtml(world, this.plan);
        break;
      case 'month':
        page = monthCloseHtml(world);
        break;
      case 'result':
        page = resultHtml(world, this.results, this.cardViews.some((v) => v.kind === 'month'));
        break;
      case 'coral':
        page = coralReportHtml(world, this.areaPlan);
        break;
      case 'findings':
        page = findingsHtml(world);
        break;
      case 'plan':
        page = operationsQueueHtml(world);
        break;
      case 'report':
        page = monthReportHtml(world);
        break;
      case 'build':
        page = buildHtml(world);
        break;
      case 'health':
        page = reefHealthHtml(world);
        break;
      case 'conditions':
        page = `<h3>Conditions and the aquarium</h3>
      <dl>
        <dt>Region</dt><dd>${escapeHtml(r.name)} (${escapeHtml(basin(r.basin).name)} basin)</dd>
        <dt>Season</dt><dd>${seasonOf(epochDayOf(world), r.hemisphere)}, ${r.hemisphere}ern hemisphere calendar</dd>
        <dt>Day length</dt><dd>${hoursMinutes(sanctuary.env.dayLengthHours)}, sunrise to sunset</dd>
        <dt>Noon sun</dt><dd>${sanctuary.env.noonSunDeg.toFixed(0)}° above the horizon</dd>
        <dt>Water</dt><dd>${fixed(sanctuary.env.seaSurfaceC, 1)} °C at the surface, ${departure(sanctuary.env.seaSurfaceAnomalyC)}, mixed to ${Math.round(sanctuary.env.mixedLayerM)} m (${escapeHtml(waterOf(sanctuary).source)}'s normal, ${escapeHtml(sanctuaryRecord(sanctuary).source)}'s record, ${escapeHtml(waterOf(sanctuary).mixedLayerSource)}'s mixed layer)</dd>
        <dt>Light</dt><dd>${Math.round(sanctuary.env.surfaceParMol)} mol of sunlight's photons a square meter a day at the surface; 1% of it reaches ${Math.round(depthOfShare(sanctuary.env.kdPar, 0.01))} m (${escapeHtml(lightOf(sanctuary).parSource)}'s sunlight, ${escapeHtml(lightOf(sanctuary).kdSource)}'s clarity, Argo's fading)</dd>
        ${heatwaveRow(sanctuary, epochDayOf(world))}
        ${reefHeatRow(sanctuary, epochDayOf(world))}
        <dt>Aquarium</dt><dd>${this.aquariumStyle === 'map'
          ? `${sanctuary.aquarium.widthKm} km by ${sanctuary.aquarium.lengthKm} km, its real seafloor`
          : `drawn as an exhibit of its waters, not to scale: its real floors, stretched${this.inScene ? '; shown now, one of its habitats in a scene of its own, as the Drawn row says' : ''}`}</dd>
        ${seafloorRows(seafloorOf(sanctuary))}
        ${this.drawingNote ? `<dt>Drawn</dt><dd>${escapeHtml(this.drawingNote)}${this.drawingRuler ? ', against the ruler on the aquarium' : ''}${this.drawingChoices ? `. Not to scale, each a rendering choice: ${escapeHtml(this.drawingChoices)}` : ''}</dd>` : ''}
      </dl>
      ${latest ? `<p class="log"><strong>${escapeHtml(plainTitle(latest.title))}.</strong> ${escapeHtml(plainTitle(latest.detail))}</p>` : ''}`;
        break;
    }
    const html = `${back}${page}`;
    if (html === this.cardHtml && key === this.cardDrawn) return;
    // The control with the keyboard's focus given it back on the page drawn again (its review: a Tab to a row was lost
    // each day).
    const focused = this.focusedKey();
    // The folds the player opened kept open on the same page drawn again (M291's review: the page's dates move each day,
    // so a daily redraw shut "Manage patrol" before its Stop could be pressed), known by their summaries' words.
    // A fold with its own name (`data-fold`, M293: the action cards' "Why this works", "Why?" since M313) is known by it.
    const foldKey = (d: HTMLElement): string | null => d.dataset.fold ?? d.querySelector(':scope > summary')?.textContent ?? null;
    const opened = key === this.cardDrawn ? [...this.cardBody.querySelectorAll<HTMLElement>('details[open]')].map(foldKey) : [];
    this.cardHtml = html;
    this.cardBody.innerHTML = html;
    if (opened.length > 0) for (const x of this.cardBody.querySelectorAll<HTMLDetailsElement>('details')) if (opened.includes(foldKey(x))) x.open = true;
    if (view.kind === 'guide') (this.cardBody.querySelector('.guide-results') as HTMLElement).innerHTML = this.guideHtml = guideHtml(fieldGuide(world, this.guideSearch));
    // The page's scroll given back once its whole page is drawn (its second review: the guide's, set before its list,
    // was clamped to its box's height, the top).
    if (key !== this.cardDrawn) this.scroller.scrollTop = this.cardScrolls[this.cardViews.length - 1] ?? 0;
    this.cardScrolls.length = this.cardViews.length - 1;
    this.cardDrawn = key;
    this.refocus(focused);
    paintSprites(this.cardBody);
  }

  /** The names of the card's pages, for the way back to one. */
  private pageNames(world: World): Parameters<typeof viewName>[1] {
    return {
      indicator: (id) => this.indicatorsOf(world).find((i) => i.id === id)?.label ?? 'Overview',
      community: (id) => COMMUNITIES.find((c) => c.id === id)?.name ?? 'Community',
      species: (id) => displayName(speciesOf(id)),
    };
  }

  /** The card's control with the keyboard's focus, by what it does, or null. */
  private focusedKey(): string | null {
    return document.activeElement instanceof HTMLElement && this.cardBody.contains(document.activeElement) ? focusKey(document.activeElement) : null;
  }

  /** The focus given back to that control on the page drawn anew, the card not scrolled to it (its second review: a focus scrolled the card back to it each day; M182's reviewer: the management page's dropdowns lost it at each choice). */
  private refocus(key: string | null): void {
    if (key) [...this.cardBody.querySelectorAll<HTMLElement>('button, input, select, summary')].find((el) => focusKey(el) === key)?.focus({ preventScroll: true });
  }

  /** The objective's or the strip's press over: their held redraw drawn once its click has been handled. */
  private releaseBar(): void {
    window.clearTimeout(this.barRelease);
    if (!this.barPressed) return;
    this.barPressed = false;
    if (this.barWaiting && this.worldNow) {
      this.barWaiting = false;
      this.drawTop(this.worldNow);
      this.drawObjective(this.worldNow);
    }
  }

  /** The card's press over: its held redraw drawn once the click it began has been handled. */
  private releaseCard(): void {
    window.clearTimeout(this.cardRelease);
    if (!this.cardPressed) return;
    this.cardPressed = false;
    if (this.cardWaiting) window.setTimeout(() => { if (this.worldNow) this.drawCard(this.worldNow); }, 0);
  }

  /** A page of the card opened from the field guide's menu entry or an animal's card (M160), on a phone its sheet shown, the world map shut over it (its review). */
  openPage(view: CardView): void {
    if (this.root.querySelector<HTMLElement>('.world-map')?.hidden === false) this.showWorldMap(false);
    this.markPage(view);
    this.cardViews = view.kind === 'overview' ? [view] : [{ kind: 'overview' }, view];
    this.cardScrolls = [];
    this.showSanctuarySheet();
    if (this.worldNow) {
      this.drawCard(this.worldNow);
      this.measured = null;
    } else this.invalidate();
  }

  /** Manage shown (M237), at least halfway open, where the card's pages are. */
  private showSanctuarySheet(): void {
    this.showPane('manage', this.sheetState === 'collapsed' ? (this.layout === 'sheet' ? 'full' : 'half') : this.sheetState);
  }

  /** Every region, every day: the oceans the player is not looking at are still running. */
  private drawRegions(world: World): void {
    const home = world.sanctuaries[0]?.regionId;
    const rows = world.regions.map((state) => {
      const r = region(state.id);
      return `<tr class="${state.id === home ? 'here' : ''}"><td>${escapeHtml(r.name)}</td>`
        + `<td>${seasonOf(epochDayOf(world), r.hemisphere)}</td><td class="num">${hoursMinutes(state.env.dayLengthHours)}</td>`
        + `<td class="num">${fixed(state.env.seaSurfaceC, 1)}° <span class="dep">${signed(state.env.seaSurfaceAnomalyC, 1)}</span></td></tr>`;
    }).join('');
    this.regionsNote.innerHTML = `${REGIONS.length} regions, all running. ${escapeHtml(pacific(world.climate, epochDayOf(world)))}
      Day length at the middle of each band; sea, the band's mean surface temperature, °C, and how far it is from
      the normal (World Ocean Atlas 2023 and NOAA ERSST v5).`;
    this.regionsRows.innerHTML = rows;
    // The Tagged list (M148), the simulation's read-out.
    setMarkup(this.regionsCard.querySelector('.tagged-list') as HTMLElement, taggedHtml(tagRecords(world)));
  }

  private onTopClick(event: Event): void {
    const target = (event.target as HTMLElement).closest<HTMLElement>('[data-speed], [data-act]');
    if (!target) return;
    const speed = target.dataset.speed;
    if (speed !== undefined) {
      this.togglePopover(this.root.querySelector('.speed-menu'), this.speedPick, false);
      this.handlers.setSpeed(Number(speed) as SpeedSetting);
      return;
    }
    const act = target.dataset.act;
    if (act === 'menu') this.menu.press('menu');
    // During an advance the clock reads paused; Pause pressed then stops the advance and stays paused (M240's second review).
    if (act === 'play') this.handlers.setSpeed(this.speed === PAUSED && !this.advancing ? this.lastRunning : PAUSED);
    if (act === 'speeds') this.togglePopover(this.root.querySelector('.speed-menu'), this.speedPick);
    if (act === 'next') this.handlers.nextEvent?.();
    if (act === 'what-next') this.whatNext();
    // The strip's funds and work (M294): the accounts, or the plan and the work under way, on Manage.
    if (act === 'status') {
      this.showPane('manage', this.layout === 'sheet' ? 'full' : 'half');
      this.openPage({ kind: target.dataset.kind } as CardView);
    }
  }

  /**
   * What should I do next? (M262, the brief's section 5: "a persistent What should I do next? option"): the objective
   * card shown whole, its why opened, its button lit a moment, and the objective said.
   */
  private whatNext(): void {
    if (this.sheetState === 'collapsed' && this.layout === 'side') this.showPane(this.pane, 'half');
    this.root.dataset.objective = 'open';
    this.measured = null;
    const button = this.root.querySelector<HTMLElement>('.objective [data-objective]:not([data-objective="toggle"])');
    if (button) {
      button.hidden = false;
      this.pulse(button);
    }
    if (this.objectiveTitle) this.say(`Next: ${this.objectiveTitle}.`);
  }

  /** A button lit a moment, the one to press (M262: "Highlight the relevant button when introducing a mechanic"). */
  private pulse(el: Element | null): void {
    if (!el) return;
    el.classList.remove('pulse');
    void (el as HTMLElement).offsetWidth;
    el.classList.add('pulse');
  }

  /**
   * How the aquarium is drawn: the card's row says so, and the button hung under the top bar, over the
   * aquarium, names the other way (the one a press switches to); hidden where the site has no exhibit to
   * switch to (M86's review, of the diorama then).
   */
  setView(style: AquariumStyle, switchable = true): void {
    const button = this.root.querySelector<HTMLButtonElement>('.view-toggle');
    if (button) {
      // "Show the" left off on a phone held sideways, where the bar has one row (`style.css`).
      button.innerHTML = `<span class="long">Show the </span>${style === 'map' ? 'exhibit' : 'map'}`;
      button.hidden = !switchable;
    }
    if (style === this.aquariumStyle) return;
    this.aquariumStyle = style;
    this.measured = null;
    this.invalidate();
  }

  /**
   * The exhibit's habitats as a strip of buttons under the top bar (M94): the whole tank first, then each habitat,
   * the one the camera is on lit; none where the aquarium is drawn as its map. Built once a view, never redrawn
   * by the day (M9).
   */
  setHabitats(habitats: readonly HabitatChoice[] | null, current: string): void {
    const strip = this.root.querySelector<HTMLElement>('.habitats');
    const fit = this.root.querySelector<HTMLElement>('.fit-all');
    if (!strip || !fit) return;
    strip.hidden = habitats === null;
    fit.hidden = habitats === null || this.inScene;
    const choices = habitats ? [...habitats, { id: 'overview', name: 'Whole tank' }] : [];
    this.habitatChoices = choices;
    const key = choices.map((c) => c.id).join(',');
    if (strip.dataset.built !== key) {
      strip.dataset.built = key;
      strip.innerHTML = choices.map((c) => `<button data-habitat="${escapeHtml(c.id)}">${escapeHtml(c.name)}</button>`).join('');
      this.measured = null;
    }
    this.highlightHabitat(current);
  }

  /**
   * A habitat's own scene shown, or the whole tank's again (M110): in a scene its Reset view; Fit all the whole tank's
   * alone. A habitat is one scene: the Reef's places are no longer chosen among (M118, Maui Nui Reef).
   */
  setScene(inScene: boolean): void {
    const reset = this.root.querySelector<HTMLElement>('.reset-view');
    const fit = this.root.querySelector<HTMLElement>('.fit-all');
    const strip = this.root.querySelector<HTMLElement>('.habitats');
    if (!reset || !fit || !strip) return;
    this.inScene = inScene;
    reset.hidden = !inScene;
    fit.hidden = inScene || strip.hidden;
    this.measured = null;
  }

  /** A habitat chosen, from the view's menu, Explore's cards or the 3D view's substitute (M288): shown, lit, and the reef's dive marked. */
  pickHabitat(id: string): void {
    this.highlightHabitat(this.handlers.chooseHabitat(id));
    if (id === 'reef') this.markSeen('exploredReef');
  }

  /**
   * Whether the 3D view is drawn (M288): without it (no WebGL, or `?render=off`) the controls only it has a use for are
   * taken away (the hour and Watch One Day, Fit all, Reset view and the other way of drawing, a card's follow and its
   * way on), the card's Stop following named Close, and Explore told so; nothing of the game's own goes with them.
   */
  setGraphics(on: boolean): void {
    if (this.graphics === on) return;
    this.graphics = on;
    this.root.dataset.graphics = on ? 'on' : 'off';
    const close = this.root.querySelector<HTMLElement>('.wildlife button[data-act="unfollow"]');
    if (close) {
      close.textContent = on ? 'Stop following' : 'Close';
      close.setAttribute('aria-label', on ? 'Stop following' : 'Close');
    }
    this.exploreHtml = '';
    this.measured = null;
    if (this.worldNow) this.drawExplore(this.worldNow);
  }

  /** The camera moved from its habitat by the player, or onto an animal: the dropdown on "Your view", so any habitat can be chosen again. */
  habitatLeft(): void {
    this.highlightHabitat('');
  }

  private highlightHabitat(current: string): void {
    for (const b of this.root.querySelectorAll<HTMLElement>('.habitats button')) b.classList.toggle('on', b.dataset.habitat === current);
    this.habitatNow = current;
    // The view's button named for what the camera shows (M237): the habitat, the whole tank, or the player's own view.
    const name = this.habitatChoices.find((c) => c.id === current)?.name ?? (current === 'all' ? 'All of the tank' : current === '' ? 'Your view' : 'The aquarium');
    const el = this.root.querySelector<HTMLElement>('.habitat-name');
    if (el) setText(el, name);
    if (this.worldNow) this.drawExplore(this.worldNow);
  }

  /**
   * The aquarium's hour on its button, "Noon · 12:00 PM", and the moon's lit share in its panel (M95). Called every
   * frame; the text is set only when it changes, and no button is rebuilt (M9).
   */
  setObservation(presetName: string, clock: string, moonLit: number, where = ''): void {
    // Its two parts' text set in place, never the button rebuilt, so a press on it is never lost as its clock runs
    // (M96's review: rebuilt, 0 of 10 presses on its name took during a watch); the preset's name left off on a
    // phone held sideways, where the bar has one row (`style.css`).
    const long = this.root.querySelector<HTMLElement>('.observe-toggle .long');
    const time = this.root.querySelector<HTMLElement>('.observe-toggle .clock');
    if (long) setText(long, `${presetName} · `);
    if (time) setText(time, clock);
    // The panel's line, as the brief gives it: "12 June 2034 · observing at 12:00 PM · Maui Nui" (M104).
    const line = this.root.querySelector<HTMLElement>('.observe-menu .observe-where');
    if (line && where) setText(line, where);
    const moon = this.root.querySelector<HTMLElement>('.observe-menu .moon');
    const moonText = `The moon tonight ${Math.round(moonLit * 100)}% lit.`;
    if (moon && moon.textContent !== moonText) moon.textContent = moonText;
    for (const b of this.root.querySelectorAll<HTMLElement>('.observe-menu .presets button')) b.classList.toggle('on', b.textContent === presetName);
  }

  /**
   * Watch One Day's bar (M96): shown while a day is watched, its progress, and Pause or Resume as it is playing.
   * Called every frame; it sets only what changed.
   */
  /** Whether a day is being watched (M96), as `setWatching` last told it. */
  private watchingDay = false;

  setWatching(state: { progress: number; playing: boolean; locked: boolean } | null): void {
    this.watchingDay = state !== null;
    const bar = this.root.querySelector<HTMLElement>('.watch');
    if (!bar) return;
    if (bar.hidden !== (state === null)) {
      bar.hidden = state === null;
      this.measured = null;
    }
    if (!state) return;
    const progress = bar.querySelector<HTMLProgressElement>('progress');
    if (progress && Math.abs(progress.value - state.progress) > 0.001) progress.value = state.progress;
    const pause = bar.querySelector<HTMLElement>('button[data-watch="pause"], button[data-watch="resume"]');
    const label = state.playing ? 'Pause' : 'Resume';
    if (pause && pause.textContent !== label) {
      pause.textContent = label;
      pause.dataset.watch = state.playing ? 'pause' : 'resume';
    }
    const lock = bar.querySelector<HTMLElement>('button[data-watch="lock"], button[data-watch="unlock"]');
    const lockLabel = state.locked ? 'Unlock camera' : 'Lock camera';
    if (lock && lock.textContent !== lockLabel) {
      lock.textContent = lockLabel;
      lock.dataset.watch = state.locked ? 'unlock' : 'lock';
    }
  }

  /** The world map shown or hidden (M98), drawn at its own size as it opens. */
  showWorldMap(open: boolean): void {
    const map = this.root.querySelector<HTMLElement>('.world-map');
    if (!map) return;
    map.hidden = !open;
    // The aquarium's bar hidden while the map covers the aquarium: it stands over the panels (its hour's menu over a
    // phone's sheet, M104) and on a phone it had covered the map's title, its Humpback whales and its Close (the
    // science session's report, M110).
    const bar = this.root.querySelector<HTMLElement>('.aquarium-bar');
    if (bar) bar.classList.toggle('under-map', open);
    // The frame rate meter stepping aside while the map is open, and the bars measured again either way, so it hangs
    // under the bar's own bottom once the map closes (M166's review: measured while the bar was hidden, it had stayed
    // behind the bar, a paused game never measuring it again).
    this.root.classList.toggle('map-open', open);
    this.measured = null;
    if (open) {
      this.drawWorldMap();
      this.fillMapCard();
    }
  }

  /** The map painted afresh at its canvas's size, the site picked marked. */
  private drawWorldMap(): void {
    const canvas = this.root.querySelector<HTMLCanvasElement>('.world-canvas');
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const r = canvas.getBoundingClientRect();
    const scale = window.devicePixelRatio || 1;
    canvas.width = Math.max(1, Math.round(r.width * scale));
    canvas.height = Math.max(1, Math.round(r.height * scale));
    ctx.setTransform(scale, 0, 0, scale, 0, 0);
    drawWorldMap(ctx, r.width, r.height, this.ownSiteId, this.pickedSite, humpbackWays(r.width, r.height), this.worldNow ? taggedSites(this.worldNow) : []);
  }

  /** A site picked on the map: its card, and the way to its aquarium where there is one. */
  private pickSite(siteId: SiteId): void {
    this.pickedSite = siteId;
    this.mapCard = 'site';
    this.drawWorldMap();
    this.fillMapCard();
  }

  /** The Hawaii humpbacks tracked (M99): their card in the site's place, no site picked. */
  private trackHumpbacks(): void {
    this.pickedSite = null;
    this.mapCard = 'humpbacks';
    this.drawWorldMap();
    this.fillMapCard();
  }

  /** The card beside the map, as the world stands today: the site picked's, or the humpbacks'. */
  private fillMapCard(): void {
    if (this.mapCard === 'site' && this.pickedSite) {
      const card = siteCard(this.pickedSite, this.ownSiteId, this.worldNow ? siteNow(this.worldNow, this.pickedSite) : []);
      const go = this.showMapCard(card);
      if (go) {
        go.hidden = card.status === 'unbuilt';
        go.textContent = card.status === 'yours' ? 'Go to its aquarium' : 'Visit its aquarium';
      }
    } else if (this.mapCard === 'humpbacks') {
      const go = this.showMapCard(humpbackCard(this.whaleNow));
      if (go) go.hidden = true;
    }
  }

  /** A card beside the map, its rows escaped; its button, for the caller to set. */
  private showMapCard(card: { title: string; rows: readonly (readonly [string, string])[] }): HTMLButtonElement | null {
    const box = this.root.querySelector<HTMLElement>('.world-site');
    if (!box) return null;
    box.hidden = false;
    setText(box.querySelector<HTMLElement>('.world-site-title') as HTMLElement, card.title);
    (box.querySelector('dl') as HTMLElement).innerHTML = card.rows.map(([k, v]) => `<dt>${escapeHtml(k)}</dt><dd>${escapeHtml(v)}</dd>`).join('');
    return box.querySelector<HTMLButtonElement>('button[data-act="go"]');
  }

  /**
   * The note over a visited site's aquarium (M98), or none: whose it is not, what is drawn of it, and the way back to
   * the player's own. Set when the visit changes.
   */
  setVisit(visited: { name: string; note: string } | null, homeName: string): void {
    const note = this.root.querySelector<HTMLElement>('.visit-note');
    if (!note) return;
    note.hidden = visited === null;
    if (visited) {
      setText(note.querySelector<HTMLElement>('.visit-text') as HTMLElement, `Visiting ${visited.name}: ${visited.note}`);
      setText(note.querySelector<HTMLElement>('button[data-act="home"]') as HTMLElement, `Back to ${homeName}`);
    }
    this.measured = null;
  }

  /**
   * The followed school's card (M97, Follow Wildlife), or none. Called every frame; its text set only when it
   * changes, and its rows escaped.
   */
  setWildlife(card: WildlifeCard | null): void {
    const box = this.root.querySelector<HTMLElement>('.wildlife');
    if (!box) return;
    const key = card ? JSON.stringify(card) : '';
    if (box.dataset.shown === key) return;
    box.dataset.shown = key;
    box.hidden = card === null;
    if (!card) {
      this.wildlifeTag = null;
      this.wildlifeSpecies = null;
      return;
    }
    setText(box.querySelector<HTMLElement>('.wildlife-title') as HTMLElement, card.title);
    (box.querySelector('.wildlife-rows') as HTMLElement).innerHTML = card.rows.map(([k, v]) => `<dt>${escapeHtml(k)}</dt><dd>${escapeHtml(v)}</dd>`).join('');
    (box.querySelector('button[data-act="route"]') as HTMLButtonElement).hidden = card.route !== true;
    const onward = box.querySelector('button[data-act="onward"]') as HTMLButtonElement;
    onward.hidden = !card.onward;
    setText(onward, card.onward ?? '');
    this.wildlifeTag = card.tag ?? null;
    // Its species' page, where the registry holds it (M160).
    const species = card.species ?? card.tag?.species;
    this.wildlifeSpecies = SPECIES.some((x) => x.id === species) ? species ?? null : null;
    (box.querySelector('button[data-act="guide"]') as HTMLButtonElement).hidden = this.wildlifeSpecies === null;
    (box.querySelector('.tag-form') as HTMLElement).hidden = card.tag === undefined;
    // None for the octopus, which lives alone (M158's review: its card offered to name its school).
    (box.querySelector('button[data-act="name-school"]') as HTMLButtonElement).hidden = card.tag?.island === undefined || card.tag.stage !== 'adult' || card.tag.species === 'octopus';
  }

  /** The sanctuary the card tells, by its id (the rename button holds it). */
  private cardSanctuary(): number {
    return Number(this.cardRename.dataset.id);
  }

  /** The campaign's mark a page opened makes (M239): the coral read; a survey's findings (M262) only once one is home. Its controls told the first time (M262). */
  private markPage(view: CardView): void {
    const tip = PAGE_TIPS[view.kind];
    if (tip) this.tip(view.kind, tip);
    const seen = markOf(view);
    if (seen && (seen !== 'readThreats' || (this.worldNow && lastSurvey(this.worldNow)))) this.markSeen(seen);
  }

  /** A mark for the campaign of what the player saw (M239), sent once, its word not said; the findings' again for each survey home since (M319). */
  private markSeen(mark: 'exploredReef' | 'readCoral' | 'readThreats'): void {
    const was = this.worldNow?.campaign.marks[mark];
    const home = mark === 'readThreats' && this.worldNow ? lastSurvey(this.worldNow) : null;
    if (was === undefined || (home !== null && was < home.day)) this.handlers.command({ type: 'campaign_mark', mark });
  }

  /**
   * The next event's button (M240): its words set in place, never rebuilt (M9), shown where there is one; pressed while
   * an advance runs, it stops it.
   */
  /** Whether an advance is under way, as the button last told it: Pause pressed then stops it and stays paused (M240's second review). */
  private advancing = false;

  setNextEvent(next: { label: string; busy: boolean } | null): void {
    const button = this.root.querySelector<HTMLButtonElement>('.next-event');
    if (!button) return;
    button.hidden = next === null;
    if (next && button.textContent !== next.label) button.textContent = next.label;
    button.classList.toggle('busy', next?.busy ?? false);
    const was = this.advancing;
    this.advancing = next?.busy ?? false;
    if (was !== this.advancing) this.highlightFollowClock();
    // Its whole words in its title and its label, the button's width cutting them (review 29).
    const words = next ? (next.busy ? `${next.label}: press to stop` : `Run on to the next event: ${next.label}`) : '';
    if (button.title !== words) { button.title = words; button.setAttribute('aria-label', words); }
  }

  /** A press on a tagged animal's way to it, in World's list or Explore's (M203; M237: in both), followed there, or why not. */
  private goToTagged(target: HTMLElement): void {
    const go = target.closest<HTMLElement>('[data-go]');
    if (!go) return;
    const why = this.handlers.goToTag(Number(go.dataset.go));
    if (why) this.say(why);
  }

  /** A press on the operations page (M238): the plan changed and drawn again, or a command sent. */
  private operate(op: string, id: string, world: World, n = 1): void {
    const plan = planFor(world, this.plan);
    if (op === 'plan-kind' && plan) this.plan = { ...plan, kind: id as SurveyKind, crew: defaultCrew(world, id as SurveyKind) };
    else if (op === 'plan-days' && plan) this.plan = { ...plan, days: Number(id) };
    else if (op === 'plan-crew' && plan) this.plan = { ...plan, crew: plan.crew.includes(Number(id)) ? plan.crew.filter((c) => c !== Number(id)) : [...plan.crew, Number(id)] };
    else if (op === 'dispatch' && plan) {
      const result = this.handlers.command({ type: 'start_survey', kind: plan.kind, days: plan.days, crew: plan.crew });
      if (result.ok) this.plan = null;
      this.say(result.message);
      if (result.ok) window.setTimeout(() => this.tip('clock', 'Days run on their own. Pause and the speed are at the top; Advance skips to the survey\'s return.'), 3600);
    } else if (op === 'hire') {
      // A hire offered by what it opens (M308): its people hired one by one, stopped at the first refusal, its reason told once.
      let result = this.handlers.command({ type: 'hire', role: id as Role });
      // Each one hired named, as the survey card's hire tells them (its review: a pair's first diver went unsaid).
      const said = [result.message];
      for (let k = 1; k < n && result.ok; k++) { result = this.handlers.command({ type: 'hire', role: id as Role }); said.push(result.message); }
      this.say(said.join(' '));
    }
    else if (op === 'let-go') this.say(this.handlers.command({ type: 'let_go', staffId: Number(id) }).message);
    this.invalidate();
    this.drawCard(world);
  }

  /** A management command (M182) through the command boundary, its word said and the card drawn again. */
  /**
   * One of the reef's actions (M327, `docs/ONE_REEF_ROADMAP.md`'s O2): a survey of the whole reef, a protected area for
   * all of Maui Nui, a patrol's next stretch where the most is taken, a cleanup of the shore with the most nets, the
   * volunteers across it; Manage's home, the reef's card, shown after. Whether the core took it.
   */
  private reefAct(what: string, kind?: string, island?: string): boolean {
    const world = this.worldNow;
    if (!world) return false;
    let done = false;
    if (what === 'survey' && (kind === 'reefBenthic' || kind === 'reefFish' || kind === 'reefBleaching')) {
      // The whole reef's, or the findings' closer count on one island (M330, O5).
      done = this.manage({ type: 'plan_survey', kind, island: (REEF_ISLANDS as readonly string[]).includes(island ?? '') ? island as ReefIsland : null, days: kind === 'reefBleaching' ? ECONOMY.assessmentDays : SURVEY_DAYS, biologists: this.areaPlan.biologists });
    } else if (what === 'protect') done = this.manage({ type: 'propose_rule', rule: { type: 'protect_herbivores', island: 'all', protect: true } });
    else if (what === 'patrol') {
      const next = nextStretch(world);
      done = next !== null && this.manage({ type: 'set_patrol', sanctuaryId: this.cardSanctuary(), island: next.island, level: 1, days: ECONOMY.patrolTerms.at(-1) as number });
    } else if (what === 'cleanup') done = this.manage({ type: 'plan_survey', kind: 'shoreCleanup', island: netsShore(world).island, days: SHORE_DAYS, biologists: this.areaPlan.biologists });
    else if (what === 'volunteers') done = this.manage({ type: 'start_volunteers', island: 'all' });
    // Inside the projects (M331, O6): the cleanup at sea kept out a trip's days more; a patrol kept a term more.
    else if (what === 'extend') done = this.manage({ type: 'extend_trip', days: SHORE_DAYS });
    else if (what === 'renew' && island) {
      const level = world.sanctuaries[0]?.management?.patrols[REEF_ISLANDS.indexOf(island as ReefIsland)] ?? 0;
      done = level > 0 && this.manage({ type: 'set_patrol', sanctuaryId: this.cardSanctuary(), island, level, days: ECONOMY.patrolTerms.at(-1) as number });
    }
    this.showPane('manage', this.layout === 'sheet' ? 'full' : 'half');
    this.openPage({ kind: 'overview' });
    return done;
  }

  private manage(command: Command): boolean {
    // A patrol begun where none ran (M295): its day kept for its island's "Done", which the world does not keep.
    const begun = command.type === 'set_patrol' && command.level > 0 && this.worldNow !== null && (managementOf(this.worldNow)?.islands.find((x) => x.island === command.island)?.patrol.level ?? 0) === 0 ? this.worldNow : null;
    const result = this.handlers.command(command);
    if (result.ok && begun && command.type === 'set_patrol') this.patrolStarts.set(begun, { ...this.patrolStarts.get(begun), [command.island]: epochDayOf(begun) });
    this.say(result.message);
    // What follows over years told apart from what changed today (M262: "Show immediate operational consequences
    // separately from long-term ecological changes"), after a protection or a patrol chosen (one reef, M327: the reef's
    // card, the Protect page gone).
    if (result.ok && (command.type === 'propose_rule' || command.type === 'set_patrol')) {
      window.clearTimeout(this.laterTimer);
      this.laterTimer = window.setTimeout(() => this.say('Over years: the fish respond slowly, and only surveys can show it. Count the reef again now to know where it stands; later counts show the change.'), 3600);
    }
    this.invalidate();
    return result.ok;
  }
  private laterTimer = 0;

  /** The speed a month's summary held the clock from (M289), given back by its Continue. */
  private speedBeforeMonth: SpeedSetting = PAUSED;

  /**
   * A month closed (M289, the playtest's fifth point: the report had been tucked away): its summary on Manage, over the
   * game, the clock held until Continue; none while the menu is open, or once the player has turned them off.
   */
  monthClosed(): void {
    if (this.menuOpen || monthSummariesOff()) return;
    // A result's card open (the month's last day's, M320): the summary under it, read after it, the clock it held given to
    // the summary to give back (M325, the casual year's H7: the annual review's card, 31 December, had been dropped for
    // the month's, its decision pressed and the review never seen).
    if (this.cardViews.at(-1)?.kind === 'result') {
      // The clock running under the result's card (its review: the days run on past it): held now, that speed the
      // summary's to give back; else the speed the result held.
      if (this.speed !== PAUSED) {
        this.speedBeforeMonth = this.speed;
        this.handlers.setSpeed(PAUSED);
      } else this.speedBeforeMonth = this.speedBeforeResult;
      this.speedBeforeResult = PAUSED;
      // One summary under it, the latest month's (its review: each month run on had added another).
      const result = this.cardViews.at(-1) as CardView;
      this.cardViews = [...this.cardViews.slice(0, -1).filter((v) => v.kind !== 'month'), { kind: 'month' }, result];
      if (this.worldNow) this.drawCard(this.worldNow);
      return;
    }
    this.speedBeforeMonth = this.speed;
    if (this.speed !== PAUSED) this.handlers.setSpeed(PAUSED);
    this.openPage({ kind: 'month' });
  }

  /**
   * The month's summary put away with the world it summed up (review M289's): a Load or a New game drops it, and the
   * speed it held, so its Continue cannot run the next world on.
   */
  dropMonth(): void {
    this.speedBeforeMonth = PAUSED;
    // A result's card too (M320), the world it told of gone.
    this.results = [];
    this.speedBeforeResult = PAUSED;
    if (this.cardViews.some((v) => v.kind === 'month' || v.kind === 'result')) {
      this.cardViews = [{ kind: 'overview' }];
      if (this.worldNow) this.drawCard(this.worldNow);
    }
  }

  /** The results the card tells (M320), and the speed they held the clock from. */
  private results: GameEvent[] = [];
  private speedBeforeResult: SpeedSetting = PAUSED;

  /**
   * A result home (M320, the casual year's H2): its card over the game, the clock held until Continue, as the month's
   * summary is; a survey's findings among them read as the findings' page reads them (`readThreats`).
   */
  resultsCame(events: readonly GameEvent[]): void {
    if (events.length === 0) return;
    const open = this.cardViews.at(-1)?.kind === 'result';
    // More the same day: added to the card; a later day's, while the card stands (the days run on past it), in its place,
    // told as any arrival is, the clock held and Manage shown (M320's review: swapped in unseen, the clock running).
    this.results = open && this.results[0]?.day === events[0]?.day ? [...this.results, ...events] : [...events];
    if (!open) this.speedBeforeResult = this.speed;
    if (this.speed !== PAUSED) this.handlers.setSpeed(PAUSED);
    this.showPane('manage', this.layout === 'sheet' ? 'full' : 'half');
    // Over a month's summary, kept under it (M325): its Continue shows the summary again.
    if (!open && this.cardViews.at(-1)?.kind === 'month') {
      this.cardViews = [...this.cardViews, { kind: 'result' }];
      if (this.worldNow) this.drawCard(this.worldNow);
    } else if (!open) this.openPage({ kind: 'result' });
    else if (this.worldNow) this.drawCard(this.worldNow);
    if (this.worldNow?.operations.finished.some((f) => isSurveyHome(f) && events.some((e) => e.title === f.title))) this.markSeen('readThreats');
  }

  /** The result card closed: the page under it again, the clock given back where it held it (none over a month's summary, which holds it itself). */
  private resultContinue(): void {
    this.results = [];
    if (this.cardViews.at(-1)?.kind === 'result') this.cardViews.pop();
    if (this.cardViews.length === 0) this.cardViews = [{ kind: 'overview' }];
    if (this.speed === PAUSED && this.speedBeforeResult !== PAUSED && !this.watchingDay && !this.cardViews.some((v) => v.kind === 'month')) this.handlers.setSpeed(this.speedBeforeResult);
    this.speedBeforeResult = PAUSED;
    if (this.worldNow) this.drawCard(this.worldNow);
  }

  /** The month's summary closed: the clock given back where the summary held it, Manage at its home or the island's card. */
  private monthContinue(off: boolean): void {
    if (off) setMonthSummariesOff();
    // Not over a day being watched, which holds the clock itself (review M289's: Continue had ended the watch).
    if (this.speed === PAUSED && this.speedBeforeMonth !== PAUSED && !this.watchingDay) this.handlers.setSpeed(this.speedBeforeMonth);
    this.speedBeforeMonth = PAUSED;
    // A result's card under it (M320: the same day's) shown again, not dropped; else Manage's home or the island's card.
    const under = this.cardViews.findIndex((v) => v.kind === 'result');
    this.cardViews = under >= 0 ? this.cardViews.slice(0, under + 1) : [{ kind: 'overview' }];
    if (this.worldNow) this.drawCard(this.worldNow);
  }

  /** A rename goes through the command boundary like everything else. */
  private onCardClick(event: Event): void {
    // The month's summary (M289): Continue, its full report, or no more summaries.
    if ((event.target as HTMLElement).closest('[data-act="result-continue"]')) {
      this.resultContinue();
      return;
    }
    const month = (event.target as HTMLElement).closest<HTMLElement>('[data-act="month-continue"], [data-act="month-off"]');
    if (month) {
      this.monthContinue(month.dataset.act === 'month-off');
      return;
    }
    // A goal's recommended way on Manage's home (M278), as the objective card's.
    const goal = (event.target as HTMLElement).closest<HTMLElement>('[data-act="goal"]');
    if (goal?.dataset.goal) {
      // From a result's card (M321: a chapter's first step), or the month's (M322: its decision), the card put away as by
      // its Continue first.
      if (this.cardViews.at(-1)?.kind === 'result') this.resultContinue();
      else if (this.cardViews.at(-1)?.kind === 'month') this.monthContinue(false);
      this.objectiveAction(goal.dataset.goal);
      return;
    }
    // A page opened, or the way back (M160).
    const press = (event.target as HTMLElement).closest<HTMLElement>('[data-act="open"], [data-act="back"]');
    if (press) {
      if (press.dataset.act === 'back') {
        if (this.cardViews.length > 1) this.cardViews.pop();
      } else {
        const kind = press.dataset.kind as CardView['kind'];
        const id = press.dataset.id ?? '';
        const view = (PLAIN_PAGES.includes(kind) ? { kind } : { kind, id }) as CardView;
        this.markPage(view);
        // Its page's scroll kept for the way back (its review: Back went to the top of the page).
        this.cardScrolls[this.cardViews.length - 1] = this.scroller.scrollTop;
        this.cardViews = kind === 'overview' ? [view] : [...this.cardViews, view];
      }
      if (this.worldNow) this.drawCard(this.worldNow);
      this.measured = null;
      return;
    }
    const target = event.target as HTMLElement;
    // A survey's return skipped to (M262: "Advance to its return").
    const ahead = target.closest<HTMLElement>('[data-act="advance-to"]');
    if (ahead) {
      this.handlers.advanceTo?.(Number(ahead.dataset.day), ahead.dataset.what ?? 'Arrived');
      return;
    }
    // The reef on the first day, or today's again (M287's before-and-after).
    const then = target.closest<HTMLElement>('[data-act="reef-then"]');
    if (then && this.handlers.chooseReefThen) {
      const id = then.dataset.id ?? '';
      this.highlightHabitat(this.handlers.chooseReefThen(then.dataset.on === '1', (REEF_ISLANDS as readonly string[]).includes(id) ? (id as ReefIsland) : null));
      return;
    }
    // The reef's actions (M327, one reef), each reaching all of Maui Nui.
    const reefAct = target.closest<HTMLElement>('[data-act="reef"]');
    if (reefAct) {
      this.reefAct(reefAct.dataset.what ?? '', reefAct.dataset.kind, reefAct.dataset.island);
      return;
    }
    // A survey planned for an island (M274), its days and divers the card's; or those changed.
    const areaSurvey = target.closest<HTMLElement>('[data-act="area-survey"]');
    if (areaSurvey) {
      const kind = areaSurvey.dataset.kind as SurveyKind;
      // The heat's bleaching check is the one island survey left (M327, one reef), at the assessment's days.
      const island = areaSurvey.dataset.id as ReefIsland;
      const days = kind === 'reefBleaching' ? Math.min(this.areaPlan.days, ECONOMY.assessmentDays) : this.areaPlan.days;
      this.manage({ type: 'plan_survey', kind, island, days, biologists: this.areaPlan.biologists, first: areaSurvey.dataset.first === '1' });
      return;
    }
    // Divers hired from the survey's own card (M302, F1), one hire each, the plan kept so its survey can go.
    const hireDivers = target.closest<HTMLElement>('[data-act="hire-divers"]');
    if (hireDivers) {
      // Stopped at the first refusal, its reason told once (M302's review: one toast for each diver short).
      for (let n = Number(hireDivers.dataset.n ?? 0); n > 0 && this.manage({ type: 'hire', role: 'biologist' }); n--);
      return;
    }
    const areaPlan = target.closest<HTMLElement>('[data-act="area-plan"]');
    if (areaPlan) {
      this.areaPlan = { days: Number(areaPlan.dataset.days ?? this.areaPlan.days), biologists: Number(areaPlan.dataset.biologists ?? this.areaPlan.biologists) };
      if (this.worldNow) this.drawCard(this.worldNow);
      return;
    }
    // The boat's plan changed (M274), and the heat's choice to keep it.
    const replan = target.closest<HTMLElement>('[data-act="replan"]');
    if (replan) {
      this.manage({ type: 'replan', id: Number(replan.dataset.id), to: replan.dataset.to === 'first' ? 'first' : 'cancel' });
      return;
    }
    if (target.closest('[data-act="keep-plan"]')) {
      this.manage({ type: 'keep_plan_in_heat' });
      return;
    }
    // The second boat ordered, and an island's volunteers set up or ended (M277).
    if (target.closest('[data-act="order-boat"]')) {
      this.manage({ type: 'order_boat' });
      return;
    }
    // A patrol level bought, or a limit proposed (M238: buttons, no dropdowns).
    const choice = target.closest<HTMLElement>('[data-act="patrol"], [data-act="limit"]');
    if (choice) {
      if (choice.getAttribute('aria-pressed') === 'true') return;
      const id = choice.dataset.id ?? '';
      const option = Number(choice.dataset.option);
      if (choice.dataset.act === 'patrol') this.manage({ type: 'set_patrol', sanctuaryId: this.cardSanctuary(), island: id, level: option, days: choice.dataset.days ? Number(choice.dataset.days) : null });
      else this.manage({ type: 'propose_rule', rule: { type: 'set_limit', group: id.slice(0, id.lastIndexOf('-')) as LimitGroup, limit: id.endsWith('-bag') ? 'bag' : 'size', option } });
      return;
    }
    // The operations page (M238): the survey planned, sent; staff hired and let go.
    const op = target.closest<HTMLButtonElement>('[data-op]');
    if (op && !op.disabled && this.worldNow) {
      this.operate(op.dataset.op ?? '', op.dataset.id ?? '', this.worldNow, Math.max(1, Number(op.dataset.n ?? 1)));
      return;
    }
    if (target.dataset.act !== 'rename') return;
    const name = window.prompt('Rename the sanctuary');
    if (name === null) return;
    const result = this.handlers.command({ type: 'rename_sanctuary', sanctuaryId: Number(target.dataset.id), name });
    this.say(result.message);
    this.invalidate();
  }
}

/** How long after a pointer is let go off the card's own click its held redraw waits, ms (JUDGED: past a tap's click, which follows its pointerup in a later task). */
const CARD_RELEASE_MS = 300;

/** Where the tips already given are remembered (M262), and whether they are turned off. */
const TIPS_KEY = 'seven-seas-tips';

/** What a page's controls do, told the first time it is opened (M262, the brief's section 5: "Explain controls when the player first encounters them"). */
const PAGE_TIPS: Readonly<Partial<Record<CardView['kind'], string>>> = {
  overview: 'Manage\'s home: your money, the reef\'s main concern, and what you can do now. More detail is under More.',
  survey: 'The survey, its crew and its days are chosen for you. Launch it, or change them under Advanced options.',
  findings: 'What the survey found, in plain words first. The counts and their errors are under Scientific details.',
  plan: 'Each boat does one survey at a time, with its captain and divers; the rest wait in turn. Put one first, or take it out.',
};

/** How long a message is shown before the next waiting may take its place (JUDGED: two short sentences read). */
const TOAST_READ_MS = 2500;

/** The card's pages that take no id. */
/** The player's choice to see no month's summary at its close (M289), kept in this browser; shown where storage fails. */
const MONTH_SUMMARY_KEY = 'seven-seas.month-summary-off';
function monthSummariesOff(): boolean {
  try { return localStorage.getItem(MONTH_SUMMARY_KEY) === '1'; } catch { return false; }
}
function setMonthSummariesOff(): void {
  try { localStorage.setItem(MONTH_SUMMARY_KEY, '1'); } catch { /* No storage: shown again next month. */ }
}

/** The first dive's tip, with the 3D view and without it (`ViewSubstitute`'s list of the habitat's animals). */
const DIVE_TIP = 'Drag to look around, pinch or scroll to zoom, and tap a fish to follow it.';
const DIVE_TIP_PANEL = 'The 3D view is off, so the reef is a list: choose an island, then tap an animal to read its card or tag it.';
const PLAIN_PAGES: readonly string[] = ['overview', 'details', 'conditions', 'management', 'operations', 'campaign', 'review', 'guide', 'coral', 'survey', 'findings', 'plan', 'report', 'build', 'health', 'month'];

/** A control on the card by what it does, to find it again on the page drawn anew. */
function focusKey(el: HTMLElement): string {
  return [el.tagName, el.dataset.act ?? '', el.dataset.kind ?? '', el.dataset.id ?? '', el.className].join('|');
}

/** A departure from the normal in words: "0.4 °C above its normal". */
function departure(c: number): string {
  const text = fixed(Math.abs(c), 1);
  return text === '0.0' ? 'at its normal' : `${text} °C ${c > 0 ? 'above' : 'below'} its normal`;
}

/** The heatwave row, while one is under way (M24). */
function heatwaveRow(s: Sanctuary, epochDay: number): string {
  const h = heatwaveToday(s, epochDay);
  if (!h) return '';
  const today = h.ratio > 1 ? `category ${categoryOf(h.ratio)}` : 'below the threshold today, easing';
  return `<dt>Heatwave</dt><dd>day ${h.day}, ${today}; at worst category ${categoryOf(s.heatwave.peakRatio)} (Hobday et al.)</dd>`;
}

/**
 * The reefs' heat stress, where it is followed (M46): NOAA Coral Reef
 * Watch's level, its Degree Heating Weeks and the day's HotSpot; and while
 * there is heat, each depth's, in its cooler water (M49).
 */
function reefHeatRow(s: Sanctuary, epochDay: number): string {
  const h = heatStressToday(s, epochDay);
  if (!h) return '';
  const hotSpot = h.hotSpotC > 0 ? `the surface ${fixed(h.hotSpotC, 1)} °C above the warmest month's normal` : 'the surface under the warmest month\'s normal';
  const level = h.level === 'No stress' ? 'none' : `${h.level.toLowerCase()} (${escapeHtml(ALERT_MEANINGS[h.level])})`;
  const reef = reefOf(s);
  const depths = reef && h.dhw > 0
    ? `; on the reef ${binHeatFallC(reef).map((f, k) => `${fixed(degreeHeatingWeeks(s.heatStress, f), 1)} at ${binSpan(k)}`).join(', ')} (${escapeHtml(reef.waterSource)})`
    : '';
  return `<dt>Reef heat</dt><dd>${level}: ${fixed(h.dhw, 1)} degree heating weeks at the surface, ${hotSpot} (NOAA Coral Reef Watch's measure)${depths}</dd>`;
}


/**
 * The bullethead parrotfish's picture on its species page (M163, the user's choice: moved there from the card's row, with
 * each group's length): each group's sprite at its mean length now (M75), from the user's pictures; none where none are.
 */
export function bulletheadPicture(s: Sanctuary, epochDay: number): string {
  if (!s.fish) return '';
  const figures = fishFigures(fishCounts(BULLETHEAD, s.fish.bullethead, epochDay).lengthMm);
  return figures ? `<dl class="picture">${figures}</dl>` : '';
}







/** The Pacific's year, and whether it is the record's or played out as an earlier one. */
function pacific(climate: ClimateState, epochDay: number): string {
  const phase = climate.phase === 'neutral' ? 'a neutral year' : `${phaseName(climate.phase)}`;
  const how = climate.analogYear === climate.ensoYear ? 'as observed'
    : regionsObserved(epochDay) ? `as observed so far, then as ${years(climate.analogYear)} was`
      : `playing out as ${years(climate.analogYear)} did`;
  return `The Pacific: ${phase}, ${years(climate.ensoYear)}, ${how}.`;
}

/** A seafloor never changes, so its summary is read once. */
const summaries = new WeakMap<Seafloor, SeafloorSummary>();

/** The sanctuary card's seafloor lines: its depth and land, and how its floor divides among the ocean's layers. */
function seafloorRows(floor: Seafloor | null): string {
  if (!floor) return '<dt>Seafloor</dt><dd>not built yet: it comes with this sea (Phase 7); the box\'s height is not a depth</dd>';
  let s = summaries.get(floor);
  if (!s) {
    s = summarize(floor);
    summaries.set(floor, s);
  }
  const summary = s;
  const zones = DEPTH_ZONES
    .filter((z) => summary.floorPercent[z.zone] >= 0.5)
    .map((z) => `${Math.round(summary.floorPercent[z.zone])}% ${z.zone}`)
    .join(', ');
  return `<dt>Seafloor</dt><dd>${Math.round(summary.deepestM).toLocaleString('en-US')} m at the deepest; `
    + `${Math.round(summary.landPercent)}% of the square is land (${escapeHtml(floor.source)})</dd>`
    + `<dt>Floor</dt><dd>${zones}</dd>`;
}

/** Writes text only when it changed, so an unchanged element is left alone. */
function setText(el: HTMLElement, text: string): void {
  if (el.textContent !== text) el.textContent = text;
}
